// ============================================================
// PADEL24 TOURNAMENT-FORMAT ENGINE — moved out of legacy/App.jsx
// (incremental TS split, mirrors the elo/booking/km/seriespill precedent).
//
// Pure group/phase/finals/standings/promotion logic for the Padel24 format:
// the player-count presets + court/capacity/pacing estimators, group makers,
// the slot model (concrete/tba/placeholder), the cross-pair / inner-group draw
// engine, the phase/finals schedule builders (buildFullSchedule + applyPacingMode),
// placeholder resolution, the configurable ranking comparator, standings helpers,
// mid-tournament player replacement / placeholder fill, group ranking,
// phase bonuses, the back-to-back conflict fixer, the phase draw functions,
// promotion/demotion (full + per-block) and the final ranking.
//
// No storage, no React, no ELO-engine calls: these functions treat `player.elo`
// only as a plain numeric field for seeding. The PlatformEngine/Padel24Engine
// facade object and applyPadel24Result now live elsewhere in src/ (applyPadel24Result
// was interleaved among the React components). The React component LadderIcon,
// the ELO-weight display helpers (ELO_WEIGHT_LEVELS/eloWeightInfo) and the
// single-match cluster (calcSingleMatchWinner/sumGames/calcSingleMatchEloChange/
// applySingleMatchElo/createSingleMatch) were NOT moved here — they are separate
// concerns that lived in the same region of the former monolith.
//
// This module must NEVER import from the former monolith. Inline comments were
// originally in Norwegian (bokmål); loosely-shaped tournament / match / group /
// slot / player objects are typed as `any` on purpose (strict:false,
// noImplicitAny:false).
// ============================================================

// getTournamentWinners (moved out of legacy/App.jsx, used by ArchiveView and the
// archive components) derives an Americano/Mexicano winner from the standings —
// import the standings calculator from the americano module. One-way dependency
// (americano never imports padel24), so no cycle.
import { timeToMinutes } from './time.js';
import { calcAmericanoStandings } from './americano.js';

export const PADEL24_PRESETS = [
  {
    id: '48p',
    players: 48,
    groups: 4,
    ppg: 12,
    label: '48 spillere',
    desc: '4 grupper à 12 — alle cross-koblet (A+B, C+D)',
    minCourts: 4,
    oddGroup: false,
  },
  {
    id: '60p',
    players: 60,
    groups: 5,
    ppg: 12,
    label: '60 spillere',
    desc: '5 grupper à 12 — siste gruppe (E) spiller innad i fase 1',
    minCourts: 5,
    oddGroup: true,
  },
  {
    id: '72p',
    players: 72,
    groups: 6,
    ppg: 12,
    label: '72 spillere',
    desc: '6 grupper à 12 — alle cross-koblet (A+B, C+D, E+F)',
    minCourts: 6,
    oddGroup: false,
  },
  {
    id: '84p',
    players: 84,
    groups: 7,
    ppg: 12,
    label: '84 spillere',
    desc: '7 grupper à 12 — siste gruppe (G) spiller innad i fase 1',
    minCourts: 7,
    oddGroup: true,
  },
  {
    id: '96p',
    players: 96,
    groups: 8,
    ppg: 12,
    label: '96 spillere',
    desc: '8 grupper à 12 — alle cross-koblet',
    minCourts: 8,
    oddGroup: false,
  },
  {
    id: '108p',
    players: 108,
    groups: 9,
    ppg: 12,
    label: '108 spillere',
    desc: '9 grupper à 12 — siste gruppe (I) spiller innad i fase 1',
    minCourts: 9,
    oddGroup: true,
  },
  {
    id: '120p',
    players: 120,
    groups: 10,
    ppg: 12,
    label: '120 spillere',
    desc: '10 grupper à 12 — alle cross-koblet',
    minCourts: 10,
    oddGroup: false,
  },
  {
    id: '132p',
    players: 132,
    groups: 11,
    ppg: 12,
    label: '132 spillere',
    desc: '11 grupper à 12 — siste gruppe (K) spiller innad i fase 1',
    minCourts: 11,
    oddGroup: true,
  },
  {
    id: '144p',
    players: 144,
    groups: 12,
    ppg: 12,
    label: '144 spillere',
    desc: '12 grupper à 12 — alle cross-koblet',
    minCourts: 12,
    oddGroup: false,
  },
  {
    id: '156p',
    players: 156,
    groups: 13,
    ppg: 12,
    label: '156 spillere',
    desc: '13 grupper à 12 — siste gruppe (M) spiller innad i fase 1',
    minCourts: 13,
    oddGroup: true,
  },
  {
    id: '168p',
    players: 168,
    groups: 14,
    ppg: 12,
    label: '168 spillere',
    desc: '14 grupper à 12 — alle cross-koblet',
    minCourts: 14,
    oddGroup: false,
  },
  {
    id: '180p',
    players: 180,
    groups: 15,
    ppg: 12,
    label: '180 spillere',
    desc: '15 grupper à 12 — siste gruppe (O) spiller innad i fase 1',
    minCourts: 15,
    oddGroup: true,
  },
  {
    id: '192p',
    players: 192,
    groups: 16,
    ppg: 12,
    label: '192 spillere',
    desc: '16 grupper à 12 — full kapasitet, alle cross-koblet',
    minCourts: 16,
    oddGroup: false,
  },
];

// "Fewer, larger groups" mode for large tournaments (84+ players).
//
// The default is 12 per group. With many players you get many groups, and
// a player in a low group must climb through many "floors" to reach the
// top. Larger groups = fewer groups to climb through + more players are
// promoted/demoted per phase (33%), so it becomes easier to climb far.
//
// Group-size requirement: must be divisible by 4 (4 players per match) and
// divide the player count evenly. We aim for the NUMBER of groups closest to
// 6, but never fewer than 5 (3 groups with 40+ players gets too big).
//
// Returns { playersPerGroup, groups, promotionCount } or null if there is no
// meaningful larger-groups alternative (≠ 12).
export function getLargeGroupConfig(players) {
  if (!players || players < 84) return null;
  const kandidater = [];
  for (let ng = 5; ng <= 12; ng++) {
    const gs = players / ng;
    if (Number.isInteger(gs) && gs % 4 === 0) {
      kandidater.push({ ng, gs });
    }
  }
  if (kandidater.length === 0) return null;
  // Sort: closest to 6 groups, on a tie → fewer groups
  kandidater.sort((a, b) => {
    const da = Math.abs(a.ng - 6),
      db = Math.abs(b.ng - 6);
    if (da !== db) return da - db;
    return a.ng - b.ng;
  });
  const best = kandidater[0];
  // If the best alternative is 12 per group, there is no real "larger" mode
  if (best.gs === 12) return null;
  return {
    playersPerGroup: best.gs,
    groups: best.ng,
    promotionCount: Math.max(1, Math.round(best.gs / 3)), // 33% up/down
  };
}

// Suitability assessment of a preset — four escalating levels:
//   niva 1 'Ikke optimalt' — mildest (odd-numbered groups). The card is NOT
//          grayed out; just a discreet, neutral note. Works perfectly fine.
//   niva 2 'Mindre egnet'  — many small groups, but manageable.
//   niva 3 'Lite egnet'    — many groups, clearly harder.
//   niva 4 'Frarådes'      — very many groups.
// niva 2-4 are shown as a grayed-out card. Returns null for fully recommended presets.
export function getPresetSuitability(preset) {
  if (!preset) return null;
  const g = preset.groups;
  // Many small groups (84+ without a larger variant) — escalating concern.
  if (preset.players >= 84 && !getLargeGroupConfig(preset.players)) {
    if (g <= 7) return { niva: 2, label: 'Mindre egnet', color: '#a16207', bg: '#fef9c3' }; // yellow
    if (g <= 11) return { niva: 3, label: 'Lite egnet', color: '#c2410c', bg: '#ffedd5' }; // orange
    return { niva: 4, label: 'Frarådes', color: '#b91c1c', bg: '#fee2e2' }; // red
  }
  // Odd-numbered groups — the mildest note ("gentlest"). Neutral slate color,
  // no warning — this works fine, it is just marginally less ideal.
  if (preset.oddGroup) {
    // #1144: #64748b was 4.34:1 on its ground; #617087 is the AA shade of it.
    return { niva: 1, label: 'Ikke optimalt', color: '#617087', bg: '#f1f5f9' }; // slate
  }
  return null;
}

// ============================================================
// COMPUTE COURT ALTERNATIVES for a given number of players/groups
//
// Assumptions:
//   - 12 players/group (Padel24 standard)
//   - 8 matches per player in total (2 rounds × 4 phases)
//   - Max 4 matches per player per day (5 on the finals day incl. the final)
//   - Day 1: Fri 16:00-23:00 (7h), day 2: Sat 9:00-17:00 (8h)
//   - 27 min match on Friday, 25 min on Saturday, 5 min between matches
//
// Returns an array of { courts, slotsPerDay, fredagSlutt, lørdagSlutt, type }
// where 'type' is 'minimum' | 'comfortable' | 'tight' | 'all_at_once'
// ============================================================
export function calcBaneAlternativer(players, groups) {
  if (!players || !groups || players <= 0 || groups <= 0) return [];

  const ppg = players / groups;
  if (ppg < 4 || ppg > 24) return [];

  // Total matches = players * 2 (8 matches/player, 4 players/match)
  const totalMatches = players * 2;

  // Per day: max 4 matches/player = 4 * players / 4 = players matches max per day
  // With a finals day: max 5 matches/player on the last day
  const maxMatchesPerDayPerPlayer = 4;
  const _maxMatchesDay1 = (maxMatchesPerDayPerPlayer * players) / 4; // = players for 4 matches
  const _maxMatchesDay2 = ((maxMatchesPerDayPerPlayer + 1) * players) / 4; // = 5*players/4 incl. final

  // The total matches should be split roughly half on each day (4 matches + 4+final)
  // Half the phases = 4 matches on Friday, 4 matches + final on Saturday
  const matchesDay1 = totalMatches / 2; // = players
  const matchesDay2 = totalMatches / 2; // = players

  // Slot time (match + gap)
  const slotMinDay1 = 27 + 5; // 32 min
  const slotMinDay2 = 25 + 5; // 30 min

  // Available time per day (in minutes, minus breaks)
  const dayMinutes1 = 7 * 60 - 30; // 7h - 30 min break = 390 min
  const dayMinutes2 = 8 * 60 - 30; // 8h - 30 min lunch = 450 min

  const maxSlotsDay1 = Math.floor(dayMinutes1 / slotMinDay1); // 12 slots
  const maxSlotsDay2 = Math.floor(dayMinutes2 / slotMinDay2); // 15 slots

  // Format end time
  const formatEndTime = (startHour, slotsUsed, slotMin) => {
    const totalMin = slotsUsed * slotMin;
    const endMin = startHour * 60 + totalMin;
    const h = Math.floor(endMin / 60);
    const m = endMin % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  };

  // Format duration: e.g. "07:00", "04:45", "02:10"
  const formatDuration = (totalMin) => {
    // Round to the nearest 5 min for slightly nicer numbers
    const rounded = Math.round(totalMin / 5) * 5;
    const hours = Math.floor(rounded / 60);
    const mins = rounded % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  const options = [];

  // Compute the day's ACTUAL end time using the same formula as the DayBlock
  // estimate. A day has 2 phases (standard Padel24). Each phase is spread over
  // 'courts' courts → ceil(faseKamper/courts) rotations. Every rotation except
  // the phase's LAST uses a full slot (matchDur+between); the last one uses only
  // matchDur. Plus a 30 min break between the phases. Final = extra rotations.
  const calcDaySlutt = (startHour, courts, slotMin, matchDur, faserDenneDag, harFinale) => {
    const faseKamper = players / 2; // R1+R2 for all pairs in one phase
    const rotPerPhase = courts > 0 ? Math.ceil(faseKamper / courts) : 0;
    const faseMin = rotPerPhase > 0 ? (rotPerPhase - 1) * slotMin + matchDur : 0;
    const finalKamper = players / 4; // one final match per player
    const finalRot = harFinale && courts > 0 ? Math.ceil(finalKamper / courts) : 0;
    const finalMin = finalRot > 0 ? (finalRot - 1) * slotMin + matchDur : 0;
    const pauseMin = faserDenneDag >= 2 ? 30 : 0; // break between the phases
    const totalMin = faserDenneDag * faseMin + finalMin + pauseMin;
    const endMin = startHour * 60 + totalMin;
    const h = Math.floor(endMin / 60);
    const m = endMin % 60;
    return {
      str: `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`,
      totalMin,
    };
  };

  // Helper function: court utilization for a court choice. Returns an object with
  // the number of rotations that have free courts, how many courts stand free
  // in each of them, and overall court utilization as a percentage.
  // A phase has players/2 matches spread evenly over ceil(faseKamper/courts)
  // rotations. Rotations that are not completely filled have free courts.
  const calcBaneInfo = (courts) => {
    const faseMatches = players / 2; // matches in one phase
    const nRot = Math.max(1, Math.ceil(faseMatches / courts));
    const base = Math.floor(faseMatches / nRot);
    const extra = faseMatches - base * nRot; // number of rotations with +1 match
    const smallRot = nRot - extra; // rotations with 'base' matches
    // Court utilization: actual matches / (rotations × courts)
    const utnyttelse = Math.round((faseMatches / (nRot * courts)) * 100);
    // Rotations with a free court, and how many free in each
    let rotMedLedig = 0,
      ledigePerRot = 0;
    if (base + 1 < courts) {
      // even the largest rotations have free court(s)
      rotMedLedig = nRot;
      ledigePerRot = courts - (base + 1); // the largest ones lack this many
    } else if (base < courts) {
      // only the small rotations have a free court
      rotMedLedig = smallRot;
      ledigePerRot = courts - base;
    }
    return { rotMedLedig, ledigePerRot, nRot, utnyttelse };
  };

  // For each "step" in courts:
  //   minimum (=groups): uses all 4 matches/day, ends late
  //   double (=2*groups): uses 2 matches/day, shorter
  //   tredouble (=3*groups): uses 1.5 matches/day (approx)

  // STEP 1: Minimum courts — 4 matches per day, full 24-slot tournament
  // matchesDay1 / minimum courts = slots
  const minSlotsDay1 = Math.ceil(matchesDay1 / groups);
  const minSlotsDay2 = Math.ceil((matchesDay2 + 1) / groups); // +1 for the final
  if (minSlotsDay1 <= maxSlotsDay1 && minSlotsDay2 <= maxSlotsDay2) {
    const d1 = calcDaySlutt(16, groups, slotMinDay1, 27, 2, false);
    const d2 = calcDaySlutt(9, groups, slotMinDay2, 25, 2, true);
    options.push({
      courts: groups,
      slotsDay1: minSlotsDay1,
      slotsDay2: minSlotsDay2,
      fredagSlutt: d1.str,
      lordagSlutt: d2.str,
      varighetDay1: formatDuration(d1.totalMin),
      varighetDay2: formatDuration(d2.totalMin),
      type: 'minimum',
      description: 'Full lengde, 4 kamper/dag',
      ledigeBaner: calcBaneInfo(groups),
    });
  }

  // STEP 2: ~50% more courts = approx 8 slots per day
  // STEP 3: 2× courts = 6 slots per day
  // STEP 4: 4× courts = ALL AT ONCE (but breaks the 4-per-day rule)

  const multipliers = [
    { mult: 1.5, type: 'comfortable', description: 'Mer komfortabel, kortere' },
    { mult: 2, type: 'short', description: 'Halve tiden, mer effektivt' },
    { mult: 3, type: 'fast', description: 'Veldig komprimert' },
  ];

  multipliers.forEach(({ mult, type, description }) => {
    const courts = Math.ceil(groups * mult);
    // Check whether this yields fewer slots than the previous suggestion
    const slotsD1 = Math.ceil(matchesDay1 / courts);
    const slotsD2 = Math.ceil((matchesDay2 + 1) / courts);
    // Only add it if it has fewer slots than the previous one
    const lastOpt = options[options.length - 1];
    if (lastOpt && slotsD1 >= lastOpt.slotsDay1 && slotsD2 >= lastOpt.slotsDay2) return;
    if (slotsD1 <= 0 || slotsD2 <= 0) return;

    // If this is exactly "all at once" (courts >= players/4), switch type
    const isAllAtOnce = courts >= Math.ceil(players / 4);
    const finalType = isAllAtOnce ? 'all_at_once' : type;
    const finalDesc = isAllAtOnce ? '⚡ Alle spiller samtidig' : description;

    const d1 = calcDaySlutt(16, courts, slotMinDay1, 27, 2, false);
    const d2 = calcDaySlutt(9, courts, slotMinDay2, 25, 2, true);
    options.push({
      courts,
      slotsDay1: slotsD1,
      slotsDay2: slotsD2,
      fredagSlutt: d1.str,
      lordagSlutt: d2.str,
      varighetDay1: formatDuration(d1.totalMin),
      varighetDay2: formatDuration(d2.totalMin),
      type: finalType,
      description: finalDesc,
      ledigeBaner: isAllAtOnce ? null : calcBaneInfo(courts),
    });
  });

  // STEP 5: If we don't already have "all at once", add it
  const allAtOnce = Math.ceil(players / 4);
  const hasAllAtOnce = options.some((o) => o.type === 'all_at_once');
  if (!hasAllAtOnce && allAtOnce > (options[options.length - 1]?.courts || 0)) {
    options.push({
      courts: allAtOnce,
      slotsDay1: 4,
      slotsDay2: 5, // 4 + finale
      fredagSlutt: formatEndTime(16, 4, slotMinDay1),
      lordagSlutt: formatEndTime(9, 5 + 1, slotMinDay2),
      varighetDay1: formatDuration(4 * slotMinDay1),
      varighetDay2: formatDuration(5 * slotMinDay2),
      type: 'all_at_once',
      description: '⚡ Alle spiller samtidig',
    });
  }

  return options;
}

export function getPadel24PresetByPlayers(players) {
  return PADEL24_PRESETS.find((p) => p.players === players) || null;
}

export function getPadel24PresetById(id) {
  return PADEL24_PRESETS.find((p) => p.id === id) || null;
}

// List of all supported player counts (sorted)
export const PADEL24_SUPPORTED_PLAYERS = PADEL24_PRESETS.map((p) => p.players);

// Standard time setup — always the same for Padel24
export const PADEL24_STANDARD_DAYS = {
  d1: { start: 16, end: 23, label: 'Fredag' },
  d2: { start: 9, end: 17, label: 'Lørdag' },
};

// Backward compatibility: old call sites use getPadel24Preset(courts).
// Return the first preset where minCourts <= courts.
export function getPadel24Preset(courts) {
  const matches = PADEL24_PRESETS.filter((p) => p.minCourts <= courts);
  if (matches.length === 0) return null;
  // Return the one with the most players (the most aggressive setup that fits)
  return matches[matches.length - 1];
}

// ============================================================
// PADEL24 ENGINE
// ============================================================
//
// MATCH STRUCTURE:
// Each match has two teams, each team has two "slots". A slot is either:
//   - { kind: 'concrete', playerId, originGroup }  (known player)
//   - { kind: 'placeholder', label, fromPhase, fromGroup, fromPosition }  (not decided)
//
// PHASE STRUCTURE:
//   Phase 1, round 1-2: CROSS-PAIRING — players from different groups are on the same team
//                      (known from the start: all players are concrete)
//   Phase 2+,  round 1-2: ONLY WITHIN THE GROUP (after promotion/demotion)
//                        Players are placeholders until the previous phase is finished
//   Finals: placeholders for placement 1, 2, 3 etc per group
//
// AFTER EACH PHASE (2 rounds):
//   - rank each group on points
//   - top 3 are promoted to the next group
//   - bottom 3 are demoted to the previous group
//   - the middle stay
//   - placeholders in future matches are resolved with actual players
//
// ============================================================

export const ROUNDS_PER_PHASE = 2;

// Builds groups based on ELO. Group A = strongest.
export function makeGroups(players, playersPerGroup) {
  const numGroups = Math.max(2, Math.round(players.length / playersPerGroup));
  const sorted = [...players].sort((a, b) => b.elo - a.elo);
  const groups = Array.from({ length: numGroups }, (_, i) => ({
    letter: String.fromCharCode(65 + i),
    players: [],
  }));
  const perGroup = Math.ceil(sorted.length / numGroups);
  sorted.forEach((p, i) => {
    const gIdx = Math.min(numGroups - 1, Math.floor(i / perGroup));
    groups[gIdx].players.push(p);
  });
  return groups;
}

// Computes capacity and estimates
// ------------------------------------------------------------
// CAPACITY & SCHEDULE
//
// config.days = [{ id, label, date, start, end, courts, breaks: [{startMin, durationMin, label}] }, ...]
// config.betweenMatches = 5         (minutes between matches, globally)
// day.matchDuration = 30            (minutes per match on that day)
// config.betweenMatches = 5        (minutes between the matches — the court is free so the next team can come on)
// config.targetMatchesPerPlayer = 9 (how many matches each player should get in total — incl. final)
//
// Total time per "slot" on a court = matchDuration + betweenMatches
// ------------------------------------------------------------

// Convert "HH:MM" to minutes since midnight
// Moved to @starpoint/engine so the API can use it too; re-exported here so
// every existing import keeps working. One definition, never a fork.
export { timeToMinutes } from './time.js';

export function minutesToTime(min) {
  const totalH = Math.floor(min / 60);
  const h = ((totalH % 24) + 24) % 24; // wrap 0-23
  const m = Math.round(min % 60);
  const next = totalH >= 24 ? ' +1d' : '';
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}${next}`;
}

// Convert decimal hours to "HH:MM" (e.g. 16.5 → "16:30")
export function hoursToTimeStr(h) {
  const totalHrs = Math.floor(h);
  const hrs = ((totalHrs % 24) + 24) % 24;
  const mins = Math.round((h - totalHrs) * 60);
  return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
}

// Convert "HH:MM" to decimal hours (e.g. "16:30" → 16.5)
export function timeStrToHours(s) {
  if (typeof s === 'number') return s;
  const [h, m] = s.split(':').map(Number);
  return h + (m || 0) / 60;
}

// Shorten the day name for compact display ("Fredag" -> "Fre", "Mandag" -> "Man")
//
// PRESENTATION ONLY, and characterised as-is (#519,
// tests/padel24-short-day-label.test.ts): nothing in the engine calls it, and
// nothing groups on the label it shortens. The web now names a system day
// through `Intl` (apps/web/src/lib/tournamentDayLabel.ts → displayShortDayLabel)
// and uses this only for a user's own label, so its output is unchanged.
export function shortDayLabel(label) {
  if (!label || typeof label !== 'string') return label;
  return label.slice(0, 3);
}

// Group colors — each letter gets a distinct color for visual identification
export const GROUP_COLORS = {
  A: { bg: '#f7fdde', text: '#3f6212', border: '#a3e635', accent: '#84cc16' }, // lime/green
  B: { bg: '#dbeafe', text: '#1e3a8a', border: '#60a5fa', accent: '#3b82f6' }, // blue
  C: { bg: '#fef3c7', text: '#78350f', border: '#fbbf24', accent: '#d97706' }, // orange/yellow
  D: { bg: '#fce7f3', text: '#831843', border: '#f472b6', accent: '#ec4899' }, // pink
  E: { bg: '#e0e7ff', text: '#3730a3', border: '#818cf8', accent: '#6366f1' }, // indigo
  F: { bg: '#d1fae5', text: '#065f46', border: '#34d399', accent: '#10b981' }, // emerald
  G: { bg: '#fee2e2', text: '#991b1b', border: '#f87171', accent: '#ef4444' }, // red
  H: { bg: '#ede9fe', text: '#5b21b6', border: '#a78bfa', accent: '#8b5cf6' }, // violet
  I: { bg: '#cffafe', text: '#155e75', border: '#22d3ee', accent: '#06b6d4' }, // cyan
  J: { bg: '#ffedd5', text: '#9a3412', border: '#fb923c', accent: '#f97316' }, // deep orange
};

export function groupColor(letter) {
  if (!letter) return GROUP_COLORS.A;
  const key = String(letter).charAt(0).toUpperCase();
  return GROUP_COLORS[key] || GROUP_COLORS.A;
}

export function teamEloFromSlot(team, players) {
  const p1 = team.p1.kind === 'concrete' ? players.find((p) => p.id === team.p1.playerId) : null;
  const p2 = team.p2.kind === 'concrete' ? players.find((p) => p.id === team.p2.playerId) : null;
  if (!p1 || !p2) return null;
  return Math.round((p1.elo + p2.elo) / 2);
}

// Compute group distribution — handles an uneven number of players
// Returns: { groups: [{letter, count}], totalGroups, lastGroupSize, isEven, suggestions }
export function calcGroupDistribution(totalPlayers, playersPerGroup) {
  if (totalPlayers <= 0 || playersPerGroup <= 0) {
    return { groups: [], totalGroups: 0, lastGroupSize: 0, isEven: true, suggestions: [] };
  }

  const fullGroups = Math.floor(totalPlayers / playersPerGroup);
  const remainder = totalPlayers % playersPerGroup;
  const totalGroups = remainder > 0 ? fullGroups + 1 : fullGroups;
  const lastGroupSize = remainder > 0 ? remainder : playersPerGroup;
  const isEven = remainder === 0;
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

  const groups = [];
  for (let i = 0; i < totalGroups; i++) {
    const isLast = i === totalGroups - 1 && remainder > 0;
    groups.push({
      letter: letters[i] || `G${i + 1}`,
      count: isLast ? remainder : playersPerGroup,
    });
  }

  // Suggestions for better playersPerGroup values (even numbers only, 6-20, max 10 groups)
  const suggestions = [];
  const isCurrentOddGroups = totalGroups % 2 !== 0;
  if ((!isEven || isCurrentOddGroups) && totalPlayers >= 8) {
    for (let ppg = 6; ppg <= 20; ppg += 2) {
      if (ppg === playersPerGroup) continue;
      const rem = totalPlayers % ppg;
      const nGroups = Math.ceil(totalPlayers / ppg);
      if (nGroups < 2 || nGroups > 10) continue;
      const lastSize = rem > 0 ? rem : ppg;
      const diff = ppg - lastSize;
      const currentDiff = playersPerGroup - lastGroupSize;
      const isEvenGroups = nGroups % 2 === 0;
      // Suggest if: even split + even number of groups, or a clear improvement
      if (
        (rem === 0 && isEvenGroups) ||
        rem === 0 ||
        (diff < currentDiff && diff <= 4 && isEvenGroups) ||
        (isEvenGroups && !isCurrentOddGroups ? false : diff <= currentDiff)
      ) {
        suggestions.push({
          ppg,
          nGroups,
          lastSize: rem > 0 ? rem : ppg,
          isEven: rem === 0,
          isEvenGroups,
          diff,
        });
      }
    }
    // Sort: even split first, then even number of groups, then smallest difference
    suggestions.sort((a, b) => {
      if (a.isEven !== b.isEven) return a.isEven ? -1 : 1;
      if (a.isEvenGroups !== b.isEvenGroups) return a.isEvenGroups ? -1 : 1;
      return a.diff - b.diff;
    });
  }

  return { groups, totalGroups, lastGroupSize, isEven, suggestions: suggestions.slice(0, 3) };
}

// Compute available slots and actually used time for one day
export function calcDayCapacity(day, betweenMatches, playerCount) {
  const dayStartMin = day.start * 60;
  const dayEndMin = day.end * 60;
  const totalDayMin = Math.max(0, dayEndMin - dayStartMin);

  const breakMin = (day.breaks || []).reduce((sum, b) => sum + b.durationMin, 0);
  const playableMin = Math.max(0, totalDayMin - breakMin);

  const matchDur = day.matchDuration || 30;
  const slotMin = matchDur + betweenMatches;

  // How many slots the day can offer
  const slotsPerCourt = Math.floor(playableMin / slotMin);
  const totalSlots = slotsPerCourt * day.courts;

  // How many matches does the day need based on phases (+ possible final)?
  const phases = day.phases || 0;
  const finalIncluded = day.includesFinal ? 1 : 0;
  const matchesPerPlayerThisDay = phases * ROUNDS_PER_PHASE + finalIncluded;

  // Number of matches actually needed on the day (depends on the player count)
  // Each round has ceil(playerCount/4) matches. Per phase: 2 rounds.
  const matchesPerRound = playerCount > 0 ? Math.ceil(playerCount / 4) : 0;
  const matchesNeeded = (phases * ROUNDS_PER_PHASE + finalIncluded) * matchesPerRound;

  // Actual slots used: each round takes ceil(matches/courts) slots per court.
  // We cannot have half slots — even though 15 matches on 6 courts "could" have
  // taken 2.5 slots, in practice it takes 3 slots because a slot is indivisible.
  const slotsPerRound = day.courts > 0 ? Math.ceil(matchesPerRound / day.courts) : 0;
  const totalRoundSlots = (phases * ROUNDS_PER_PHASE + finalIncluded) * slotsPerRound;

  // Actually used minutes (slots used × slot time)
  const usedMin = totalRoundSlots * slotMin;

  // "Dead time" — available minus used minus breaks
  const deadMin = Math.max(0, playableMin - usedMin);

  // Estimated end time (start + used time + all breaks before the end time)
  // For simplicity: add all breaks that are BEFORE the end time
  let endMin = dayStartMin + usedMin;
  // Breaks that fall within the used time count in
  (day.breaks || []).forEach((b) => {
    const mode = b.mode || 'time';
    if (mode === 'after_phase') {
      endMin += b.durationMin;
      return;
    }
    if (!b.start) return;
    const breakStartMin = timeToMinutes(b.start);
    if (breakStartMin >= dayStartMin && breakStartMin < endMin) {
      endMin += b.durationMin;
    }
  });

  return {
    dayId: day.id,
    label: day.label,
    date: day.date,
    totalDayMin,
    playableMin,
    breakMin,
    slotsPerCourt,
    totalSlots,
    courts: day.courts,
    matchDuration: matchDur,
    slotMin,
    phases,
    finalIncluded: !!day.includesFinal,
    matchesPerPlayerThisDay,
    matchesNeeded,
    rundePerSlot: slotsPerRound,
    totalRoundSlots,
    usedMin,
    deadMin,
    estimatedEndMin: endMin,
    estimatedEndTime: minutesToTime(endMin),
    fits: totalSlots >= matchesNeeded,
  };
}

export function calcCapacity(config, playerCount = 0) {
  const dayCapacities = (config.days || []).map((d) =>
    calcDayCapacity(d, config.betweenMatches, playerCount),
  );
  const totalSlots = dayCapacities.reduce((sum, d) => sum + d.totalSlots, 0);
  return { dayCapacities, totalSlots };
}

// Given a number of matches per player and a max per day: how many players fit?
//
// Per day: each player can have at most `maxPerDay` matches.
// That means the number of "player-match slots" per day is limited to:
//   min(day.totalSlots * 4, players * maxPerDay)
//
// Total player-matches across all days must be >= players * targetMatchesPerPlayer
//
// We find the highest number of players where this works out:
//   For each candidate size n (multiple of 4):
//     - Per-day capacity: min(day.slots * 4, n * maxPerDay) for each day
//     - Sum of available player-matches >= n * target?
export function calcMaxPlayers(dayCapacities, target, maxPerDay) {
  if (target < 1 || !dayCapacities.length) return 0;

  // Start from a high estimate and count down
  // Naively: compute for each n (in steps of 4) whether it works out
  const totalSlots = dayCapacities.reduce((s, d) => s + d.totalSlots, 0);
  const naiveMax = Math.floor((totalSlots * 4) / target);
  let best = 0;

  // Light brute-force up to naiveMax (already bounded)
  for (let n = 4; n <= naiveMax; n += 4) {
    // For each day: max player-matches = min(slots*4, n * maxPerDay)
    const totalAvailable = dayCapacities.reduce(
      (sum, d) => sum + Math.min(d.totalSlots * 4, n * maxPerDay),
      0,
    );
    if (totalAvailable >= n * target) {
      best = n;
    }
  }
  return best;
}

export function estimateSchedule(config, playerCount) {
  const cap = calcCapacity(config, playerCount);
  const totalPhasesWanted = config.totalPhases || 4;
  const wantsFinal = config.hasFinal !== false;
  const targetMatchesPerPlayer = totalPhasesWanted * ROUNDS_PER_PHASE + (wantsFinal ? 1 : 0);
  const maxPerDay = config.maxMatchesPerDay || 999;

  // Sum up phases and finals from the days (manually defined)
  const totalPhasesConfigured = (config.days || []).reduce((sum, d) => sum + (d.phases || 0), 0);
  const totalFinalsConfigured = (config.days || []).reduce(
    (sum, d) => sum + (d.includesFinal ? 1 : 0),
    0,
  );
  const matchesPerPlayer = totalPhasesConfigured * ROUNDS_PER_PHASE + totalFinalsConfigured;
  const groupMatchesPerPlayer = totalPhasesConfigured * ROUNDS_PER_PHASE;
  const phases = totalPhasesConfigured;

  const totalMatches = playerCount > 0 ? Math.floor((playerCount * matchesPerPlayer) / 4) : 0;

  // Max players = the highest number where all days can fit their phases
  // within the available time. We use the SAME time formula as the estimate:
  // each phase = (rotPerPhase-1)*slotMin + matchDur, where rotPerPhase =
  // ceil(faseKamper/courts). It matches the actual pacing.
  let maxPlayers = 0;
  for (let n = 4; n <= 1000; n += 4) {
    const fitsAllDays = cap.dayCapacities.every((d) => {
      const dPhases = d.phases || 0;
      const dFinal = d.finalIncluded ? 1 : 0;
      if (dPhases === 0 && dFinal === 0) return true;
      const courts = d.courts || 1;
      const dMatchDur = d.matchDuration || 30;
      const dSlotMin = dMatchDur + (config.betweenMatches || 5);

      // Rotations per phase: R1+R2 for all pairs spread over the courts.
      const faseKamper = Math.ceil(n / 4) * 2;
      const rotPerPhase = Math.ceil(faseKamper / courts);
      const faseMin = rotPerPhase > 0 ? (rotPerPhase - 1) * dSlotMin + dMatchDur : 0;
      // The final: one match per player spread over the courts.
      const finalRot = dFinal > 0 ? Math.ceil(Math.ceil(n / 4) / courts) : 0;
      const finalMin = finalRot > 0 ? (finalRot - 1) * dSlotMin + dMatchDur : 0;

      const trengsMin = dPhases * faseMin + finalMin;
      // d.playableMin = the day's time minus breaks. The phases must fit within it.
      return trengsMin <= d.playableMin;
    });
    if (fitsAllDays) {
      maxPlayers = n;
    } else {
      break;
    }
  }

  // Distribution — info per day
  const phaseDist = cap.dayCapacities.map((d) => ({
    dayId: d.dayId,
    label: d.label,
    phases: d.phases,
    finale: d.finalIncluded ? 1 : 0,
    matchesPerPlayer: d.matchesPerPlayerThisDay,
    exceedsMax: d.matchesPerPlayerThisDay > (d.finalIncluded ? maxPerDay + 1 : maxPerDay),
    fits: d.fits,
    deadMin: d.deadMin,
    estimatedEndTime: d.estimatedEndTime,
    estimatedEndMin: d.estimatedEndMin,
    courts: d.courts,
    matchDuration: d.matchDuration,
    usedMin: d.usedMin,
    playableMin: d.playableMin,
  }));

  return {
    ...cap,
    matchesPerPlayer,
    groupMatchesPerPlayer,
    totalMatches,
    phases,
    totalPhasesWanted,
    wantsFinal,
    targetMatchesPerPlayer,
    maxMatchesPerDay: maxPerDay,
    maxPlayers,
    phaseDist,
    finalIncluded: totalFinalsConfigured > 0,
    feasible:
      cap.dayCapacities.every((d) => d.fits) &&
      totalFinalsConfigured === (wantsFinal ? 1 : 0) &&
      phases === totalPhasesWanted,
    matchesShortage: Math.max(0, targetMatchesPerPlayer - matchesPerPlayer),
    matchesExcess: Math.max(0, matchesPerPlayer - targetMatchesPerPlayer),
    phasesShortage: Math.max(0, totalPhasesWanted - phases),
    phasesExcess: Math.max(0, phases - totalPhasesWanted),
  };
}

// ------------------------------------------------------------
// SLOT HELPERS
// ------------------------------------------------------------
// ESTIMATE PACING — computes the expected waiting time and total day time
// for each pacing mode based on the setup (players, courts, phases).
//
// Returns a dictionary with one estimate per mode:
//   { even: {...}, back_to_back: {...}, one_gap: {...} }
//
// Each estimate contains:
//   - waitBetween: real waiting time (end of a match to start of the next, minutes)
//   - waitMin/waitMax: variation
//   - phaseDuration: how long a single phase takes in minutes
//   - matchesPerPhase: number of matches in a phase
//   - slotsPerPhase: number of rounds
//   - totalDayDuration: time at the venue per day (first match to last finished)
//   - matchesPerPlayerPerDay
// ------------------------------------------------------------
export function estimatePacing(config, playerCount) {
  const playersPerGroup = config.playersPerGroup || 12;
  const numGroups = Math.max(1, Math.ceil(playerCount / playersPerGroup));
  const matchesPerGroupPerRound = Math.floor(playersPerGroup / 4); // typically 3
  const matchesPerPhasePerRound = numGroups * matchesPerGroupPerRound;
  const matchesPerPhase = matchesPerPhasePerRound * 2; // R1 + R2

  // Number of phases per day (average) — use the first day as reference
  const day = (config.days || [])[0] || {};
  const courts = day.courts || 5;
  const matchDur = day.matchDuration || 30;
  const between = config.betweenMatches || 5;
  const slotMin = matchDur + between;
  const phasesPerDay = day.phases || 2;

  // For each mode: compute the number of slots and the waiting time
  const modes: any = {};

  // ----- EVEN -----
  // R1 of ALL groups first, then R2 of all.
  // Slots per phase: ceil(R1-matches / courts) + ceil(R2-matches / courts)
  {
    const slotsR1 = Math.ceil(matchesPerPhasePerRound / courts);
    const slotsR2 = Math.ceil(matchesPerPhasePerRound / courts);
    const slotsPerPhase = slotsR1 + slotsR2;
    const phaseDuration = slotsPerPhase * slotMin;

    // Waiting time between R1 and R2 for a player:
    // Depends on where the player is in the R1 and R2 ordering.
    // Min: played last in R1, first in R2 → 1 slot apart = slotMin
    // Max: played first in R1, last in R2 → (slotsR1 + slotsR2 - 1) slots apart
    // Median: roughly half the phase length
    const waitBetweenStartToStart = slotsR1 * slotMin; // typically 3 rounds
    const waitRealMin = slotMin - matchDur; // 5 min theoretical minimum
    const waitRealMax = (slotsR1 + slotsR2 - 1) * slotMin - matchDur; // worst case
    const waitRealMed = waitBetweenStartToStart - matchDur; // realistic median

    modes.even = {
      slotsPerPhase,
      phaseDuration,
      matchesPerPhase,
      waitRealMin,
      waitRealMed,
      waitRealMax,
      // Total day: phasesPerDay phases + inter-phase buffer
      totalDayDuration: phasesPerDay * phaseDuration,
    };
  }

  // ----- BACK-TO-BACK -----
  // Each pair (R1+R2 of the same players) = 2 slots on the same court.
  // Number of blocks = ceil(numPairs / courts)
  // Slots per phase = 2 * blocks
  {
    const numPairs = matchesPerPhasePerRound; // each pair has one R1 and one R2
    const blocks = Math.ceil(numPairs / courts);
    const slotsPerPhase = 2 * blocks;
    const phaseDuration = slotsPerPhase * slotMin;

    // Waiting time: right after the previous one (slotMin apart from start)
    const waitRealConst = slotMin - matchDur; // = betweenMatches

    modes.back_to_back = {
      slotsPerPhase,
      phaseDuration,
      matchesPerPhase,
      waitRealMin: waitRealConst,
      waitRealMed: waitRealConst,
      waitRealMax: waitRealConst,
      totalDayDuration: phasesPerDay * phaseDuration,
    };
  }

  // ----- ONE GAP -----
  // 2 pairs per court package of 4 slots: R1A, R1B, R2A, R2B
  // Number of packages = ceil(numPairs / (2 * courts))
  // Slots per phase = 4 * packages (or fewer if the last package is half-full)
  {
    const numPairs = matchesPerPhasePerRound;
    const pairsPerBlock = 2 * courts;
    const blocks = Math.ceil(numPairs / pairsPerBlock);

    // Compute the number of slots: each full block is 4 slots, a half-full block is 3 slots
    let slotsPerPhase = 0;
    let remaining = numPairs;
    for (let b = 0; b < blocks; b++) {
      const inBlock = Math.min(remaining, pairsPerBlock);
      slotsPerPhase += inBlock > courts ? 4 : 3;
      remaining -= inBlock;
    }
    const phaseDuration = slotsPerPhase * slotMin;

    // Waiting time: 2 slots apart from start → 2 * slotMin - matchDur
    const waitRealConst = 2 * slotMin - matchDur;

    modes.one_gap = {
      slotsPerPhase,
      phaseDuration,
      matchesPerPhase,
      waitRealMin: waitRealConst,
      waitRealMed: waitRealConst,
      waitRealMax: waitRealConst,
      totalDayDuration: phasesPerDay * phaseDuration,
    };
  }

  return {
    numGroups,
    matchesPerPhase,
    matchesPerPhasePerRound,
    courts,
    matchDur,
    slotMin,
    phasesPerDay,
    matchesPerPlayerPerDay: phasesPerDay * 2, // 2 matches per phase per player
    modes,
  };
}

// ------------------------------------------------------------
// CONCRETE SLOT — for matches with actual players (always from the start in phase 1, after the draw in phase 2+)
// originGroup + originPhase are frozen HERE — they represent the group the player
// belonged to when the match was built, not the current group. Players can move
// group through promotion/demotion, but the icon in the match program should always
// show where they were WHEN that match was played.
// ------------------------------------------------------------
export function concreteSlot(player, originGroup, originPhase = 1) {
  return { kind: 'concrete', playerId: player.id, originGroup, originPhase };
}

// TBA slot — for matches in phase 2+ where the partner/opponent is chosen dynamically at phase start
export function tbaSlot(group, fromPhase, slotNum) {
  // fromPhase = the phase the player was RANKED in. The player belongs to the group
  // in the NEXT phase (fromPhase + 1) — that is the one we show in the icon, so a
  // phase 2 TBA shows "A2", not "A1".
  const displayPhase = fromPhase + 1;
  const groupLabel = displayPhase >= 1 ? `${group}${displayPhase}` : group;
  return {
    kind: 'tba',
    label: `TBA Grp ${groupLabel}`,
    fromPhase,
    fromGroup: group,
    slotNum,
  };
}

export function placeholderSlot(group, position, fromPhase, labelPhase = fromPhase + 1) {
  // fromPhase = the phase the player was RANKED in.
  //
  // THE LABEL IS USUALLY THE NEXT PHASE, because a player ranked in one phase is
  // promoted or relegated into the next one, and that is the group they will be
  // wearing when the match is played.
  //
  // IT IS NOT, FOR THE FINALS. There is no phase after the last one, so `+1`
  // would name a group that never existed — «Grp A5» on a four-phase
  // tournament. The finals rank on the LAST phase and are played by the groups
  // of that same phase, so they pass `labelPhase` explicitly. Carried on the
  // slot rather than recomputed by each reader, because three of them were
  // doing `fromPhase + 1` independently and would have had to agree by hand.
  const displayPhase = labelPhase;
  const groupLabel = displayPhase >= 1 ? `${group}${displayPhase}` : group;
  return {
    kind: 'placeholder',
    label: `${ordinal(position)} plass Grp ${groupLabel}`,
    fromPhase,
    labelPhase,
    fromGroup: group,
    fromPosition: position,
  };
}

export function ordinal(n) {
  if (n === 1) return '1.';
  if (n === 2) return '2.';
  if (n === 3) return '3.';
  return `${n}.`;
}

// ------------------------------------------------------------
// PHASE 1, ROUND 1-2: CROSS-PAIRING
//
// Each match has 4 players — two from one group, two from another
// We pair Group A with Group B, Group C with Group D, etc.
// (or everyone with everyone if there is an odd number, but we take pairs first)
// ------------------------------------------------------------
// ------------------------------------------------------------
// PHASE 1 DRAW — uses the same rules as phase 2+
//
// For cross-pair: each team MUST have 1 player from each group.
// For inner-pair: all 4 players from the same group (like later phases).
// ------------------------------------------------------------
export function drawPair1RoundMatches(pair, round, history, players, rules) {
  const playerById = {};
  players.forEach((p) => (playerById[p.id] = p));

  const PARTNER_PENALTY = 1000;
  const OPPONENT_PENALTY = 500;
  const HOME_AWAY_PENALTY = 50;
  const ELO_PENALTY = 1;
  const ELO_THRESHOLD = 100;
  const SPEIL_BONUS = -2500; // negative = reward for the R1 partner being an opponent in R2

  const isSpeilrotasjon = rules.mode === 'speilrotasjon';

  function pairScore(a, b, asHome) {
    let score = 0;
    // In Speilrotasjon (mirror rotation): we want to avoid the same partner (the R1 partner should not be the R2 partner)
    if ((rules.avoidSamePartner || isSpeilrotasjon) && history.partnerHistory[a]?.has(b))
      score += PARTNER_PENALTY;
    if (rules.balanceHomeAway) {
      const aBalance =
        (history.homeAwayCount[a]?.home || 0) - (history.homeAwayCount[a]?.away || 0);
      const bBalance =
        (history.homeAwayCount[b]?.home || 0) - (history.homeAwayCount[b]?.away || 0);
      if (asHome) {
        score += HOME_AWAY_PENALTY * (Math.max(0, aBalance) + Math.max(0, bBalance));
      } else {
        score += HOME_AWAY_PENALTY * (Math.max(0, -aBalance) + Math.max(0, -bBalance));
      }
    }
    return score;
  }

  function matchupScore(team1Ids, team2Ids) {
    let score = 0;
    if (isSpeilrotasjon) {
      // Speilrotasjon: reward the round 1 partner being an opponent in round 2
      team1Ids.forEach((a) => {
        const oldPartners = history.partnerHistory[a];
        if (oldPartners) {
          team2Ids.forEach((b) => {
            if (oldPartners.has(b)) score += SPEIL_BONUS;
          });
        }
      });
    }
    // avoidSameOpponent applies REGARDLESS of mode — it governs history from earlier phases
    if (rules.avoidSameOpponent) {
      team1Ids.forEach((a) =>
        team2Ids.forEach((b) => {
          if (history.opponentHistory[a]?.has(b)) score += OPPONENT_PENALTY;
        }),
      );
    }
    if (rules.balanceElo) {
      const t1p = team1Ids.map((id) => playerById[id]).filter(Boolean);
      const t2p = team2Ids.map((id) => playerById[id]).filter(Boolean);
      if (t1p.length === 2 && t2p.length === 2) {
        const t1Elo = (t1p[0].elo + t1p[1].elo) / 2;
        const t2Elo = (t2p[0].elo + t2p[1].elo) / 2;
        const diff = Math.abs(t1Elo - t2Elo);
        if (diff > ELO_THRESHOLD) score += ELO_PENALTY * (diff - ELO_THRESHOLD);
      }
    }
    return score;
  }

  const matches = [];

  if (pair.type === 'cross') {
    // Cross: each team = 1 from A + 1 from B
    const aLeft = [...pair.groups[0].players].sort(() => Math.random() - 0.5);
    const bLeft = [...pair.groups[1].players].sort(() => Math.random() - 0.5);
    const groupA = pair.groups[0].letter;
    const groupB = pair.groups[1].letter;

    while (aLeft.length >= 2 && bLeft.length >= 2) {
      let bestCombo = null;
      let bestScore = Infinity;

      const aCands = aLeft.slice(0, Math.min(8, aLeft.length));
      const bCands = bLeft.slice(0, Math.min(8, bLeft.length));

      for (let i = 0; i < aCands.length; i++) {
        for (let j = i + 1; j < aCands.length; j++) {
          for (let k = 0; k < bCands.length; k++) {
            for (let l = k + 1; l < bCands.length; l++) {
              const a1 = aCands[i],
                a2 = aCands[j],
                b1 = bCands[k],
                b2 = bCands[l];
              // Two valid splits (cross): (a1+b1) vs (a2+b2) or (a1+b2) vs (a2+b1)
              const splits = [
                { t1a: a1, t1b: b1, t2a: a2, t2b: b2 },
                { t1a: a1, t1b: b2, t2a: a2, t2b: b1 },
              ];
              splits.forEach((sp) => {
                const s =
                  pairScore(sp.t1a.id, sp.t1b.id, true) +
                  pairScore(sp.t2a.id, sp.t2b.id, false) +
                  matchupScore([sp.t1a.id, sp.t1b.id], [sp.t2a.id, sp.t2b.id]);
                if (s < bestScore) {
                  bestScore = s;
                  bestCombo = { a1, a2, b1, b2, ...sp };
                }
              });
            }
          }
        }
      }

      if (!bestCombo) break;

      matches.push({
        team1: {
          p1: concreteSlot(
            bestCombo.t1a,
            bestCombo.t1a === bestCombo.a1 || bestCombo.t1a === bestCombo.a2 ? groupA : groupB,
          ),
          p2: concreteSlot(
            bestCombo.t1b,
            bestCombo.t1b === bestCombo.a1 || bestCombo.t1b === bestCombo.a2 ? groupA : groupB,
          ),
        },
        team2: {
          p1: concreteSlot(
            bestCombo.t2a,
            bestCombo.t2a === bestCombo.a1 || bestCombo.t2a === bestCombo.a2 ? groupA : groupB,
          ),
          p2: concreteSlot(
            bestCombo.t2b,
            bestCombo.t2b === bestCombo.a1 || bestCombo.t2b === bestCombo.a2 ? groupA : groupB,
          ),
        },
        groupContext: `${groupA}+${groupB}`,
        primaryGroup: groupA,
      });

      // Update history
      const ids = [bestCombo.t1a.id, bestCombo.t1b.id, bestCombo.t2a.id, bestCombo.t2b.id];
      ids.forEach((id) => {
        if (!history.partnerHistory[id]) history.partnerHistory[id] = new Set();
        if (!history.opponentHistory[id]) history.opponentHistory[id] = new Set();
        if (!history.homeAwayCount[id]) history.homeAwayCount[id] = { home: 0, away: 0 };
      });
      history.partnerHistory[bestCombo.t1a.id].add(bestCombo.t1b.id);
      history.partnerHistory[bestCombo.t1b.id].add(bestCombo.t1a.id);
      history.partnerHistory[bestCombo.t2a.id].add(bestCombo.t2b.id);
      history.partnerHistory[bestCombo.t2b.id].add(bestCombo.t2a.id);
      [bestCombo.t1a.id, bestCombo.t1b.id].forEach((a) =>
        [bestCombo.t2a.id, bestCombo.t2b.id].forEach((b) => {
          history.opponentHistory[a].add(b);
          history.opponentHistory[b].add(a);
        }),
      );
      history.homeAwayCount[bestCombo.t1a.id].home++;
      history.homeAwayCount[bestCombo.t1b.id].home++;
      history.homeAwayCount[bestCombo.t2a.id].away++;
      history.homeAwayCount[bestCombo.t2b.id].away++;

      // Remove the 4 players from the pools
      [bestCombo.a1, bestCombo.a2].forEach((p) => {
        const idx = aLeft.findIndex((x) => x.id === p.id);
        if (idx >= 0) aLeft.splice(idx, 1);
      });
      [bestCombo.b1, bestCombo.b2].forEach((p) => {
        const idx = bLeft.findIndex((x) => x.id === p.id);
        if (idx >= 0) bLeft.splice(idx, 1);
      });
    }
  } else {
    // Inner — 4 players from the same group
    const g = pair.group;
    const pool = [...g.players].sort(() => Math.random() - 0.5);

    while (pool.length >= 4) {
      let bestCombo = null;
      let bestScore = Infinity;

      const cands = pool.slice(0, Math.min(12, pool.length));

      for (let i = 0; i < cands.length; i++) {
        for (let j = i + 1; j < cands.length; j++) {
          for (let k = j + 1; k < cands.length; k++) {
            for (let l = k + 1; l < cands.length; l++) {
              const four = [cands[i], cands[j], cands[k], cands[l]];
              const splits = [
                [
                  [four[0], four[1]],
                  [four[2], four[3]],
                ],
                [
                  [four[0], four[2]],
                  [four[1], four[3]],
                ],
                [
                  [four[0], four[3]],
                  [four[1], four[2]],
                ],
              ];
              splits.forEach((split) => {
                const [t1, t2] = split;
                const s =
                  pairScore(t1[0].id, t1[1].id, true) +
                  pairScore(t2[0].id, t2[1].id, false) +
                  matchupScore([t1[0].id, t1[1].id], [t2[0].id, t2[1].id]);
                if (s < bestScore) {
                  bestScore = s;
                  bestCombo = { four, t1, t2 };
                }
              });
            }
          }
        }
      }

      if (!bestCombo) break;

      matches.push({
        team1: {
          p1: concreteSlot(bestCombo.t1[0], g.letter),
          p2: concreteSlot(bestCombo.t1[1], g.letter),
        },
        team2: {
          p1: concreteSlot(bestCombo.t2[0], g.letter),
          p2: concreteSlot(bestCombo.t2[1], g.letter),
        },
        groupContext: g.letter,
        primaryGroup: g.letter,
      });

      // Update history
      const t1Ids = bestCombo.t1.map((p) => p.id);
      const t2Ids = bestCombo.t2.map((p) => p.id);
      [...t1Ids, ...t2Ids].forEach((id) => {
        if (!history.partnerHistory[id]) history.partnerHistory[id] = new Set();
        if (!history.opponentHistory[id]) history.opponentHistory[id] = new Set();
        if (!history.homeAwayCount[id]) history.homeAwayCount[id] = { home: 0, away: 0 };
      });
      history.partnerHistory[t1Ids[0]].add(t1Ids[1]);
      history.partnerHistory[t1Ids[1]].add(t1Ids[0]);
      history.partnerHistory[t2Ids[0]].add(t2Ids[1]);
      history.partnerHistory[t2Ids[1]].add(t2Ids[0]);
      t1Ids.forEach((a) =>
        t2Ids.forEach((b) => {
          history.opponentHistory[a].add(b);
          history.opponentHistory[b].add(a);
        }),
      );
      t1Ids.forEach((id) => history.homeAwayCount[id].home++);
      t2Ids.forEach((id) => history.homeAwayCount[id].away++);

      bestCombo.four.forEach((p) => {
        const idx = pool.findIndex((x) => x.id === p.id);
        if (idx >= 0) pool.splice(idx, 1);
      });
    }
  }

  return matches;
}

export function generatePhase1Round(groups, round, _slotMinutes) {
  // Kept for backward compatibility — no longer used directly
  const matches = [];
  const numGroups = groups.length;
  const pairings = [];
  for (let i = 0; i < numGroups; i += 2) {
    if (i + 1 < numGroups) {
      pairings.push([groups[i], groups[i + 1]]);
    } else {
      pairings.push([groups[i], groups[i - 1]]);
    }
  }
  pairings.forEach(([gA, gB]) => {
    matches.push(...generatePhase1PairRound(gA, gB, round));
  });
  return matches;
}

// Generate matches for ONE group pair in a single round
export function generatePhase1PairRound(gA, gB, round) {
  const matches = [];
  const aPlayers = [...gA.players].sort((a, b) => b.elo - a.elo);
  const bPlayers = [...gB.players].sort((a, b) => b.elo - a.elo);

  const aRot = (round - 1) % aPlayers.length;
  const bRot = ((round - 1) * 3) % bPlayers.length;
  const aRotated = [...aPlayers.slice(aRot), ...aPlayers.slice(0, aRot)];
  const bRotated = [...bPlayers.slice(bRot), ...bPlayers.slice(0, bRot)];

  const minLen = Math.min(aRotated.length, bRotated.length);
  for (let i = 0; i + 1 < minLen; i += 2) {
    matches.push({
      team1: {
        p1: concreteSlot(aRotated[i], gA.letter),
        p2: concreteSlot(bRotated[i], gB.letter),
      },
      team2: {
        p1: concreteSlot(aRotated[i + 1], gA.letter),
        p2: concreteSlot(bRotated[i + 1], gB.letter),
      },
      groupContext: `${gA.letter}+${gB.letter}`,
      primaryGroup: gA.letter,
    });
  }
  return matches;
}

// ------------------------------------------------------------
// PHASE 2+, ROUND 1-2: ONLY WITHIN THE GROUP
//
// Within each group: rotate the pair constellations
// Uses placeholders because we don't know who is in the group yet
// ------------------------------------------------------------
export function generateLaterPhaseRound(numGroups, playersPerGroup, phase, round) {
  const matches = [];

  for (let gIdx = 0; gIdx < numGroups; gIdx++) {
    const group = String.fromCharCode(65 + gIdx);

    // Within a group of N players, generate N/4 matches per round
    // Rotate so each player faces different opponents
    const positions = Array.from({ length: playersPerGroup }, (_, i) => i + 1); // 1-indexed
    const rot = (phase * 7 + round * 3) % playersPerGroup;
    const rotated = [...positions.slice(rot), ...positions.slice(0, rot)];

    // Standard rotation: [0,3] vs [1,2], [4,7] vs [5,6] etc
    for (let i = 0; i + 3 < rotated.length; i += 4) {
      matches.push({
        team1: {
          p1: placeholderSlot(group, rotated[i], phase - 1),
          p2: placeholderSlot(group, rotated[i + 3], phase - 1),
        },
        team2: {
          p1: placeholderSlot(group, rotated[i + 1], phase - 1),
          p2: placeholderSlot(group, rotated[i + 2], phase - 1),
        },
        groupContext: group,
        primaryGroup: group,
      });
    }
  }

  return matches;
}

// ------------------------------------------------------------
// FINALS
//
// For each group (A, B, C, D...): placement matches based on the final group after the last phase
// 1st-2nd place, 3rd-4th place, 5th-6th place etc
// All as placeholders until the last phase is finished
// ------------------------------------------------------------
export function generateFinalsStructure(
  numGroups,
  playersPerGroup,
  lastPhase,
  finalsPairing = '1-3',
  groupPlayOrder = 'best_last',
  luckyLooser = false,
) {
  const finals = [];
  const finalsPerGroup = Math.floor(playersPerGroup / 4);

  // Numbering — Finale 1 = gold (A top), 2 = A middle, 3 = A bottom, 4 = B top ...
  //
  // GENERATION ORDER is governed by groupPlayOrder:
  //   'best_last' (default): highest finalNumber first → Finale 1 (gold) alone last (climax).
  //                          If the number of finals > the number of courts, it is Finale 1 that ends up in
  //                          the last round — not a random middle final.
  //   'best_first':          lowest finalNumber first → Finale 1 is played first.
  //
  // IMPORTANT: Iterate on tierIdx (integer), not on i. We previously used
  // `for (i = playersPerGroup - 4; i >= 0; i -= 4)` with `tierIdx = i / 4`,
  // but this produced:
  //   1) Decimal tierIdx for group sizes that are not multiples of 4
  //      (e.g. 10/group → tierIdx 1.5 and 0.5 → "Finale 1.5", "Finale 2.5")
  //   2) The top tier (place 1-4) was often skipped for non-multiples of 4
  //      (10/group generated only 2 finals per group instead of covering
  //      place 1-4 + 5-8, so the group's best 2 players got no final)
  //
  // Now we iterate on integer tierIdx → guarantees an integer finalNumber and that the top tier
  // is ALWAYS included.
  const bestFirst = groupPlayOrder === 'best_first';
  const groupIdxList = bestFirst
    ? Array.from({ length: numGroups }, (_, i) => i)
    : Array.from({ length: numGroups }, (_, i) => numGroups - 1 - i);
  const tierIdxList = bestFirst
    ? Array.from({ length: finalsPerGroup }, (_, i) => i)
    : Array.from({ length: finalsPerGroup }, (_, i) => finalsPerGroup - 1 - i);
  // Number of places per group (= playersPerGroup)
  const N = playersPerGroup;

  for (const gIdx of groupIdxList) {
    const group = String.fromCharCode(65 + gIdx);
    const harGruppeOver = gIdx > 0;
    const harGruppeUnder = gIdx < numGroups - 1;

    // Lucky Looser mapping: with lucky looser on, who ends up on each finals slot
    // is shuffled. For a group (other than A and the lowest one):
    //   Slot 1            → the last-placed player from the group ABOVE (group-above place N)
    //   Slot 2..7         → the group's own places 2..7 (unchanged)
    //   Slot 8            → the winner of the group BELOW (group-below place 1)
    //   Slot 9..N         → the group's own places 8..(N-1) — shifted down one
    // Returns which ACTUAL group + placement ends up on the slot,
    // plus a lucky hint if the player came from outside.
    const slotMapping = (pos) => {
      if (!luckyLooser) {
        return { fromGroup: group, fromPosition: pos, luckyHint: null };
      }
      // Slot 1: the last-placed player from the group above
      if (pos === 1 && harGruppeOver) {
        const overGroup = String.fromCharCode(65 + gIdx - 1);
        return {
          fromGroup: overGroup,
          fromPosition: N,
          luckyHint: { move: 'down', fromGroup: overGroup },
        };
      }
      // Slot 8: the winner of the group below
      if (pos === 8 && harGruppeUnder) {
        const underGroup = String.fromCharCode(65 + gIdx + 1);
        return {
          fromGroup: underGroup,
          fromPosition: 1,
          luckyHint: { move: 'up', fromGroup: underGroup },
        };
      }
      // Slot 9..N: the group's own places 8..(N-1) — shifted down one.
      // (Only if the group actually has a group below, otherwise no shift.)
      if (pos >= 9 && harGruppeUnder) {
        return { fromGroup: group, fromPosition: pos - 1, luckyHint: null };
      }
      // Slot 2..7 (and the rest): the group's own, unchanged
      return { fromGroup: group, fromPosition: pos, luckyHint: null };
    };
    const mkSlot = (pos) => {
      const m = slotMapping(pos);
      // SEEDED FROM THE LAST PHASE, AND LABELLED WITH IT.
      //
      // This read `lastPhase - 1`, and the caller that draws the finals computes
      // the LAST phase's ranking and stores it under `groupRankings[lastPhase]`
      // (TournamentViewActive). So the ranking made specifically to draw the
      // finals was never read: they were decided before the final phase was
      // played, and its results could not move them. Reported from a 6-court run
      // where group A's last place was in A's own lucky-looser final.
      //
      // `labelPhase` is the same number, not one more: the player is wearing the
      // group they played that phase in, which is what the label must say.
      const slot = placeholderSlot(m.fromGroup, m.fromPosition, lastPhase, lastPhase);
      return m.luckyHint ? { ...slot, luckyHint: m.luckyHint } : slot;
    };

    for (const tierIdx of tierIdxList) {
      const i = tierIdx * 4;
      const positions = [i + 1, i + 2, i + 3, i + 4];
      const finalNumber = gIdx * finalsPerGroup + tierIdx + 1;

      // Each final ranks 2 places: winners on (2N-1), losers on (2N).
      // Finale 1 → 1st and 2nd place. Finale 2 → 3rd and 4th place. Etc.
      const placeFrom = (finalNumber - 1) * 2 + 1;
      const placeTo = placeFrom + 1;
      const rankLabel = `Finale ${finalNumber} · Plass ${placeFrom}–${placeTo}`;

      let team1Idx, team2Idx;
      if (finalsPairing === '1-2') {
        team1Idx = [0, 1];
        team2Idx = [2, 3];
      } else {
        team1Idx = [0, 2];
        team2Idx = [1, 3];
      }

      finals.push({
        team1: {
          p1: mkSlot(positions[team1Idx[0]]),
          p2: mkSlot(positions[team1Idx[1]]),
        },
        team2: {
          p1: mkSlot(positions[team2Idx[0]]),
          p2: mkSlot(positions[team2Idx[1]]),
        },
        groupContext: group,
        primaryGroup: group,
        rankLabel,
        finalNumber,
        isFinal: true,
      });
    }
  }

  return finals;
}

// ------------------------------------------------------------
// MAIN FUNCTION: Build the full match plan with times and courts
// ------------------------------------------------------------
export function buildFullSchedule(config, groups, _schedule) {
  const allMatches = [];
  let matchCounter = 0;

  const playersPerGroup = groups[0]?.players.length || config.playersPerGroup;
  const numGroups = groups.length;
  const between = config.betweenMatches || 5;

  // ============================================================
  // SCHEDULER — one slot list per day (with that day's matchDuration)
  // ============================================================
  function buildDaySlotList(day) {
    const dayStart = day.start * 60;
    const dayEnd = day.end * 60;
    const matchDur = day.matchDuration || 30;
    const slotMin = matchDur + between;
    const slots = [];

    // Only 'time' mode breaks affect slot generation. 'after_phase' breaks
    // are handled separately in applyPacingMode (they are placed after a specific
    // phase and depend on how many matches the phase has).
    const breaks = [...(day.breaks || [])]
      .filter((b) => (b.mode || 'time') === 'time' && b.start)
      .map((b) => ({
        start: timeToMinutes(b.start),
        end: timeToMinutes(b.start) + b.durationMin,
        label: b.label,
      }))
      .sort((a, b) => a.start - b.start);

    function fitsBeforeBreak(t) {
      const totalEnd = t + slotMin;
      if (totalEnd > dayEnd) return false;
      for (const br of breaks) {
        if (t < br.end && totalEnd > br.start) return false;
      }
      return true;
    }

    function nextPossibleStart(t) {
      for (const br of breaks) {
        if (t >= br.start && t < br.end) return br.end;
      }
      return t;
    }

    // Generate slots from start to end, with a bit of overflow
    // so finals always get time (shown as "exceeds" in the UI)
    const overflowEnd = dayEnd + 6 * 60; // 6 extra hours as a buffer

    for (let courtIdx = 0; courtIdx < day.courts; courtIdx++) {
      let t = dayStart;
      while (t + matchDur <= overflowEnd) {
        t = nextPossibleStart(t);
        if (t + matchDur > overflowEnd) break;
        if (t < dayEnd && !fitsBeforeBreak(t)) {
          let jumped = false;
          for (const br of breaks) {
            if (t < br.start && t + matchDur > br.start) {
              t = br.end;
              jumped = true;
              break;
            }
          }
          if (!jumped) {
            t = dayEnd;
            continue;
          } // Jump to overflow
          continue;
        }
        slots.push({ startMin: t, courtIdx, slotMin });
        t += slotMin;
      }
    }
    slots.sort((a, b) => a.startMin - b.startMin || a.courtIdx - b.courtIdx);
    return slots;
  }

  // Slot list per day (so we can place matches precisely per day)
  const dayIndex = new Map(); // dayId → { day, slots, cursor }
  (config.days || []).forEach((day) => {
    dayIndex.set(day.id, {
      day,
      slots: buildDaySlotList(day),
      cursor: 0,
    });
  });

  function takeSlotOnDay(dayId, matchData) {
    const idx = dayIndex.get(dayId);
    if (!idx || idx.cursor >= idx.slots.length) {
      // Fallback if the day is full (should really not happen)
      return {
        ...matchData,
        court: '?',
        day: '?',
        date: '?',
        time: '?',
        timeMinutes: 0,
        id: `m${++matchCounter}`,
      };
    }
    const slot = idx.slots[idx.cursor++];
    return {
      ...matchData,
      court: slot.courtIdx + 1,
      day: idx.day.label,
      dayId: idx.day.id,
      date: idx.day.date,
      time: minutesToTime(slot.startMin),
      timeMinutes: slot.startMin,
      id: `m${++matchCounter}`,
    };
  }

  // After a round: jump to the next time slot on the day (all courts must finish first)
  function _alignToNextRoundOnDay(dayId) {
    const idx = dayIndex.get(dayId);
    if (!idx || idx.cursor === 0) return;

    const lastSlot = idx.slots[idx.cursor - 1];
    if (!lastSlot) return;
    const minNext = lastSlot.startMin + lastSlot.slotMin;

    while (idx.cursor < idx.slots.length && idx.slots[idx.cursor].startMin < minNext) {
      idx.cursor++;
    }
  }

  // Make sure the next match starts no earlier than minTime (to give players a break)
  function ensureMinTimeOnDay(dayId, minTime) {
    const idx = dayIndex.get(dayId);
    if (!idx) return;
    while (idx.cursor < idx.slots.length && idx.slots[idx.cursor].startMin < minTime) {
      idx.cursor++;
    }
  }

  // Get the time of the last placed slot + slotMin (when the players are finished)
  function getEndTimeOnDay(dayId) {
    const idx = dayIndex.get(dayId);
    if (!idx || idx.cursor === 0) return 0;
    const lastSlot = idx.slots[idx.cursor - 1];
    return lastSlot ? lastSlot.startMin + lastSlot.slotMin : 0;
  }

  // ============================================================
  // BUILD MATCHES WITH THE CORRECT DAY DISTRIBUTION
  //
  // Uses config.days[i].phases to know how many phases each day takes.
  // Phase 1 is always on the first day that has phases > 0 (cross-pairing).
  // ============================================================

  let phaseCounter = 1;
  let finalAssigned = false;

  // Total phases in total from all days
  const totalPhases = (config.days || []).reduce((sum, d) => sum + (d.phases || 0), 0);

  for (const day of config.days || []) {
    const dayPhases = day.phases || 0;
    if (dayPhases === 0 && !day.includesFinal) continue;

    // Place each phase that belongs to this day
    for (let p = 0; p < dayPhases; p++) {
      const currentPhase = phaseCounter++;

      if (currentPhase === 1) {
        // Phase 1: cross-pairing.
        // Generates TBA slots — the user must explicitly draw phase 1.
        const pairings = [];
        for (let i = 0; i < numGroups; i += 2) {
          if (i + 1 < numGroups) {
            pairings.push({ type: 'cross', groups: [groups[i], groups[i + 1]] });
          } else {
            pairings.push({ type: 'inner', group: groups[i] });
          }
        }
        // groups[0] = A = strongest. pairings is therefore [A+B, C+D, E+F].
        // Order is governed by groupPlayOrder:
        //   'best_last' (default): the weakest group plays first, A last
        //     (climax) → reverse to [E+F, C+D, A+B].
        //   'best_first': A plays first → keep [A+B, C+D, E+F].
        if ((config.groupPlayOrder || 'best_last') === 'best_last') {
          pairings.reverse();
        }

        // For each pair, create TBA matches for R1 and R2
        const pairR1EndTimes = [];

        pairings.forEach((pair, pi) => {
          const groupLetters =
            pair.type === 'cross'
              ? [pair.groups[0].letter, pair.groups[1].letter]
              : [pair.group.letter];
          const playerCount =
            pair.type === 'cross'
              ? pair.groups[0].players.length + pair.groups[1].players.length
              : pair.group.players.length;
          const numMatches = Math.floor(playerCount / 4);
          const phase1Pair = { type: pair.type, groupLetters };

          // R1
          for (let m = 0; m < numMatches; m++) {
            const baseNum = m * 4;
            allMatches.push(
              takeSlotOnDay(day.id, {
                team1: {
                  p1: tbaSlot(groupLetters[0], 0, baseNum + 1),
                  p2:
                    pair.type === 'cross'
                      ? tbaSlot(groupLetters[1], 0, baseNum + 2)
                      : tbaSlot(groupLetters[0], 0, baseNum + 2),
                },
                team2: {
                  p1: tbaSlot(groupLetters[0], 0, baseNum + 3),
                  p2:
                    pair.type === 'cross'
                      ? tbaSlot(groupLetters[1], 0, baseNum + 4)
                      : tbaSlot(groupLetters[0], 0, baseNum + 4),
                },
                groupContext:
                  pair.type === 'cross' ? `${groupLetters[0]}+${groupLetters[1]}` : groupLetters[0],
                primaryGroup: groupLetters[0],
                phase: 1,
                round: 1,
                phase1Pair,
                needsDraw: true,
              }),
            );
          }
          pairR1EndTimes[pi] = getEndTimeOnDay(day.id);
        });

        // R2 — preserves the same time constraint
        pairings.forEach((pair, pi) => {
          ensureMinTimeOnDay(day.id, pairR1EndTimes[pi]);
          const groupLetters =
            pair.type === 'cross'
              ? [pair.groups[0].letter, pair.groups[1].letter]
              : [pair.group.letter];
          const playerCount =
            pair.type === 'cross'
              ? pair.groups[0].players.length + pair.groups[1].players.length
              : pair.group.players.length;
          const numMatches = Math.floor(playerCount / 4);
          const phase1Pair = { type: pair.type, groupLetters };

          for (let m = 0; m < numMatches; m++) {
            const baseNum = m * 4;
            allMatches.push(
              takeSlotOnDay(day.id, {
                team1: {
                  p1: tbaSlot(groupLetters[0], 0, baseNum + 1),
                  p2:
                    pair.type === 'cross'
                      ? tbaSlot(groupLetters[1], 0, baseNum + 2)
                      : tbaSlot(groupLetters[0], 0, baseNum + 2),
                },
                team2: {
                  p1: tbaSlot(groupLetters[0], 0, baseNum + 3),
                  p2:
                    pair.type === 'cross'
                      ? tbaSlot(groupLetters[1], 0, baseNum + 4)
                      : tbaSlot(groupLetters[0], 0, baseNum + 4),
                },
                groupContext:
                  pair.type === 'cross' ? `${groupLetters[0]}+${groupLetters[1]}` : groupLetters[0],
                primaryGroup: groupLetters[0],
                phase: 1,
                round: 2,
                phase1Pair,
                needsDraw: true,
              }),
            );
          }
        });
      } else {
        // Phase 2+: within a group — LOWEST GROUPS FIRST
        // Same principle: R2 starts for a group when its R1 is finished

        // Generate all matches per group
        function genGroupMatches(gIdx, phase, _round) {
          const group = String.fromCharCode(65 + gIdx);
          // Use the ACTUAL group size (can vary between groups)
          const groupSize = groups[gIdx]?.players.length || playersPerGroup;
          const numMatches = Math.floor(groupSize / 4);
          const gMatches = [];
          for (let m = 0; m < numMatches; m++) {
            // 4 unique "slot numbers" per match, continuing sequentially
            const baseNum = m * 4;
            gMatches.push({
              team1: {
                p1: tbaSlot(group, phase - 1, baseNum + 1),
                p2: tbaSlot(group, phase - 1, baseNum + 2),
              },
              team2: {
                p1: tbaSlot(group, phase - 1, baseNum + 3),
                p2: tbaSlot(group, phase - 1, baseNum + 4),
              },
              groupContext: group,
              primaryGroup: group,
              needsDraw: true, // marked as a match that needs a draw at phase start
            });
          }
          return gMatches;
        }

        // Order is governed by groupPlayOrder:
        //   'best_last' (default): the weakest group plays first, A last (climax)
        //   'best_first':          A plays first, the weakest last
        const groupOrder = [];
        if ((config.groupPlayOrder || 'best_last') === 'best_first') {
          for (let gIdx = 0; gIdx < numGroups; gIdx++) groupOrder.push(gIdx);
        } else {
          for (let gIdx = numGroups - 1; gIdx >= 0; gIdx--) groupOrder.push(gIdx);
        }

        // R1: sequentially, fill all courts
        const groupR1EndTimes = {};
        groupOrder.forEach((gIdx) => {
          genGroupMatches(gIdx, currentPhase, 1).forEach((m) => {
            allMatches.push(takeSlotOnDay(day.id, { ...m, phase: currentPhase, round: 1 }));
          });
          groupR1EndTimes[gIdx] = getEndTimeOnDay(day.id);
        });

        // R2: start for each group when its R1 is finished
        groupOrder.forEach((gIdx) => {
          ensureMinTimeOnDay(day.id, groupR1EndTimes[gIdx]);
          genGroupMatches(gIdx, currentPhase, 2).forEach((m) => {
            allMatches.push(takeSlotOnDay(day.id, { ...m, phase: currentPhase, round: 2 }));
          });
        });
      }
    }

    // Place the final if it belongs to this day
    if (day.includesFinal && !finalAssigned) {
      // SYNCHRONIZATION: Find the max end time among all placed matches on
      // this day, and advance the cursor so the finals start after all
      // group matches are finished.
      //
      // Bug without this: If phase N ends earlier on one court (e.g.
      // group A is finished before group B on the same court), the first final
      // takes the free slot ALONE — because the other courts are still busy.
      // Result: one final isolated in its own round while the rest sit
      // together later. (Specifically reported: Finale 4 alone in one round
      // while 1, 2, 3, 5, 6 were together in the next round.)
      const placedOnThisDay = allMatches.filter((m) => m.dayId === day.id);
      if (placedOnThisDay.length > 0) {
        const dayIdx = dayIndex.get(day.id);
        const slotMin = dayIdx?.slots[0]?.slotMin || 27;
        const maxEnd = Math.max(...placedOnThisDay.map((m) => (m.timeMinutes || 0) + slotMin));
        ensureMinTimeOnDay(day.id, maxEnd);
      }

      // Finals use the total number of phases as "fromPhase" for placeholders
      // Generated with the lowest group first, filling all courts chronologically
      generateFinalsStructure(
        numGroups,
        playersPerGroup,
        totalPhases,
        config.finalsPairing || '1-3',
        config.groupPlayOrder || 'best_last',
        config.luckyLooser,
      ).forEach((m) => {
        allMatches.push(takeSlotOnDay(day.id, { ...m, phase: 'finals', round: 1 }));
      });

      // POST-PROCESS: Sort finals within each time window by finalNumber
      // (same players, same times — but court 1 = the best final)
      const finalsList = allMatches.filter((m) => m.isFinal && m.dayId === day.id);
      const byTime: any = {};
      finalsList.forEach((m) => {
        if (!byTime[m.timeMinutes]) byTime[m.timeMinutes] = [];
        byTime[m.timeMinutes].push(m);
      });
      Object.values(byTime).forEach((group: any) => {
        group.sort((a, b) => a.finalNumber - b.finalNumber);
        group.forEach((m, idx) => {
          m.court = idx + 1;
        });
      });

      finalAssigned = true;
    }
  }

  // ============================================================
  // POST-PROCESS: apply the pacing mode
  //
  // Modes:
  //  'even' — like the default scheduler, R1 of all groups first, then R2.
  //           Gives a longer waiting time between phases but even match gaps.
  //  'back_to_back' — R1 and R2 of the same players right after each other.
  //                   Shortest single waiting time (one slot), but a long break between phases.
  //  'one_gap' — R1, wait one match on the same court, R2.
  //              Good middle variant: short gap, possible to watch others' match.
  // ============================================================
  if (config.pacingMode && config.pacingMode !== 'even') {
    applyPacingMode(allMatches, config);

    // CRITICAL: pacing changes the times of phase matches, but the finals were placed
    // BEFORE pacing. If pacing pushes phase matches later in time, the finals
    // will now overlap — two matches on the same court at the same time.
    //
    // Fix: for each day, find the max end time among phase matches after pacing,
    // and shift ALL finals on that day so they start after the last phase match.
    // Preserves the relative order between finals.
    (config.days || []).forEach((day) => {
      const phaseMatchesOnDay = allMatches.filter(
        (m) => m.dayId === day.id && m.phase !== 'finals',
      );
      const finalsOnDay = allMatches.filter((m) => m.dayId === day.id && m.phase === 'finals');
      if (phaseMatchesOnDay.length === 0 || finalsOnDay.length === 0) return;

      const matchDur = day.matchDuration || 30;
      const _slotMin = matchDur + (config.betweenMatches || 5);

      // End time of the last phase match (timeMinutes + matchDur)
      const phaseMaxEnd = Math.max(
        ...phaseMatchesOnDay.map((m) => (m.timeMinutes || 0) + matchDur),
      );
      // Round up to the next slot boundary + at least one between-match gap between phase and final
      const finalsStartMin = phaseMaxEnd + (config.betweenMatches || 5);

      // Earliest start among the finals today — we want to preserve the relative distance
      const finalsMinStart = Math.min(...finalsOnDay.map((m) => m.timeMinutes || 0));

      if (finalsMinStart >= finalsStartMin) return; // no shift necessary

      const delta = finalsStartMin - finalsMinStart;
      finalsOnDay.forEach((m) => {
        m.timeMinutes = (m.timeMinutes || 0) + delta;
        m.time = minutesToTime(m.timeMinutes);
      });
    });
  }

  return allMatches;
}

// ============================================================
// DYNAMIC PACING — packs pairs onto courts with dependency-based scheduling
//
// Algorithm:
//   1) Build one combined slot grid ACROSS ALL DAYS (chronological)
//   2) Build a pair list from all matches (R1+R2 of the same players)
//   3) Compute a "ready slot idx" per pair based on dependencies:
//      - Phase 1: 0 (can start from day 1 start)
//      - Phase 2+ group G: slot after G's previous phase is finished
//   4) Greedily place pairs slot-by-slot, court-by-court:
//      - Pick the first pair (after sorting) that is ready and fits
//      - Allows a pair from phase X+1 to fill a gap in phase X slots if
//        the dependency is resolved
//      - Allows a pair to move between days if there is free space
//        on an earlier day and the dependency is resolved
//   5) Mark pairs that had to deviate from one_gap (back_to_back or wide_gap)
//      so the UI can show it
//
// Conflict model (simplified since concrete players aren't known at planning):
//   - A pair belongs to a "block" (phase 1: cross-group like "A+B" or inner "E";
//     phase 2+: a single group)
//   - Pairs from the same block can be parallelized (different players within
//     the block's members) up to floor(blockSize / 4) simultaneous pairs
//   - Pairs from different blocks that SHARE a group (e.g. phase 1 "A+B" and
//     phase 2 "A") CANNOT be parallelized
// ============================================================

export function applyPacingMode(allMatches, config) {
  const pacingMode = config.pacingMode || 'one_gap';

  // 'even' = evenly spread across the whole day, nothing to pack
  if (pacingMode === 'even') return;

  // ============================================================
  // The pacing mode governs how R1 and R2 within the same phase are placed:
  //   'one_gap' (default): R1, wait one match, R2 on the same court.
  //   'back_to_back': R1 and R2 right after each other, no gap.
  //
  // All groups must complete one phase before the next phase starts — gives
  // a predictable match plan. There used to be a "dynamic" mode that
  // packed phases more tightly across groups, but it was removed
  // because it created confusion around TBA matches.
  //
  // BREAKS: Two modes are supported:
  //   'time' (or undefined): the break starts at a specified clock time.
  //                          Built into slot generation via skipBreaks.
  //   'after_phase': the break starts right after phase X is finished playing
  //                  (no dead time). Placed between phases in applyPacingMode.
  // ============================================================

  const between = config.betweenMatches || 5;
  const dayConfigs = config.days || [];

  // Group matches per day (excluding finals)
  const byDay = new Map();
  allMatches.forEach((m) => {
    if (m.phase === 'finals') return;
    if (!byDay.has(m.dayId)) byDay.set(m.dayId, []);
    byDay.get(m.dayId).push(m);
  });

  for (const [dayId, matches] of byDay) {
    const dayCfg = dayConfigs.find((d) => d.id === dayId);
    if (!dayCfg) continue;

    const matchDur = dayCfg.matchDuration || 30;
    const slotMin = matchDur + between;
    const dayStart = dayCfg.start * 60;
    const courts = dayCfg.courts;

    // Separate out breaks by mode. 'time' breaks skip matches over
    // via skipBreaks. 'after_phase' breaks increment phaseStartTime
    // between phases without affecting slot generation within a phase.
    const allBreaks = (dayCfg.breaks || []).map((b) => ({
      id: b.id,
      mode: b.mode || 'time',
      start: b.start,
      durationMin: b.durationMin,
      afterPhase: b.afterPhase,
      label: b.label,
    }));
    const timeBreaks = allBreaks
      .filter((b) => b.mode === 'time' && b.start)
      .map((b) => ({
        ...b,
        startMin: timeToMinutes(b.start),
        endMin: timeToMinutes(b.start) + b.durationMin,
      }))
      .sort((a, b) => a.startMin - b.startMin);
    const phaseBreaks = allBreaks.filter((b) => b.mode === 'after_phase');

    // Helper: given a raw linear slot start time, skip over time breaks so
    // the slot does not overlap. A slot "overlaps" if the match
    // (start to start+matchDur) crosses the break interval.
    const skipBreaks = (t) => {
      let current = t;
      let changed = true;
      while (changed) {
        changed = false;
        for (const br of timeBreaks) {
          if (current < br.endMin && current + matchDur > br.startMin) {
            current = br.endMin;
            changed = true;
          }
        }
      }
      return current;
    };

    // Get the phase order and pair up R1/R2 of the same players within the same group
    const phaseMap = new Map();
    matches.forEach((m) => {
      if (!phaseMap.has(m.phase)) phaseMap.set(m.phase, new Map());
      const grpMap = phaseMap.get(m.phase);
      const key = m.primaryGroup || m.groupContext || 'x';
      if (!grpMap.has(key)) grpMap.set(key, { r1: [], r2: [] });
      const b = grpMap.get(key);
      if (m.round === 1) b.r1.push(m);
      else if (m.round === 2) b.r2.push(m);
    });

    const sortedPhases = Array.from(phaseMap.keys()).sort((a, b) => a - b);

    // For each phase: build its "pair list" and place it with the phaseStart time
    let phaseStartTime = dayStart;

    for (const phase of sortedPhases) {
      const grpMap = phaseMap.get(phase);

      // Sort the pair keys by PLAY ORDER ON THE DAY.
      //
      // groupPlayOrder = 'best_last' (default): the weakest group plays first,
      //   A is played last (climax).
      // groupPlayOrder = 'best_first': A plays first, the weakest last.
      //
      // For 'best_last', "played last" means A's pairs should end up in the
      // LATER slot times. We sort the pairs in the order we want them to
      // be PLAYED (earliest first). With 'best_last': E, C, A. With
      // 'best_first': A, C, E.
      const groupPlayOrder = config.groupPlayOrder || 'best_last';
      const sortedKeys = Array.from(grpMap.keys()).sort((a: any, b: any) => {
        if (groupPlayOrder === 'best_first') {
          // A first → alphabetical
          return a.localeCompare(b);
        }
        // best_last: A last → reverse alphabetical
        return b.localeCompare(a);
      });

      const pairs = [];
      for (const k of sortedKeys) {
        const b = grpMap.get(k);
        b.r1.forEach((r1m, i) => {
          pairs.push({ r1: r1m, r2: b.r2[i] });
        });
      }

      const numPairs = pairs.length;

      // Compute how many slots the phase needs and then place it
      if (pacingMode === 'back_to_back') {
        // Each pair = 2 slots on the same court (R1 + R2)
        // With C courts we can parallelize C pairs per "block" of 2 slots
        // Number of blocks = ceil(numPairs / courts)
        const blocks = Math.ceil(numPairs / courts);

        // Build the slot-time array respecting breaks. Total slots in this
        // phase = blocks * 2 (R1 + R2 per block).
        const totalSlots = blocks * 2;
        const slotTimes = [];
        let t = phaseStartTime;
        for (let i = 0; i < totalSlots; i++) {
          t = skipBreaks(t);
          slotTimes.push(t);
          t += slotMin;
        }

        let maxSlotUsed = -1;
        pairs.forEach((pair, pIdx) => {
          const courtIdx = pIdx % courts;
          const blockIdx = Math.floor(pIdx / courts);
          const baseSlot = blockIdx * 2; // 2 slots per block

          const r1Time = slotTimes[baseSlot];
          const r2Time = slotTimes[baseSlot + 1];
          maxSlotUsed = Math.max(maxSlotUsed, baseSlot + 1);

          pair.r1.court = courtIdx + 1;
          pair.r1.timeMinutes = r1Time;
          pair.r1.time = minutesToTime(r1Time);

          if (pair.r2) {
            pair.r2.court = courtIdx + 1;
            pair.r2.timeMinutes = r2Time;
            pair.r2.time = minutesToTime(r2Time);
          }
        });

        // The next phase starts directly after the last ACTUALLY USED slot is finished
        // (not the last slot in the array — it may be empty if the phase doesn't fill
        // the whole block). No "between" since the break/phase transition replaces it.
        phaseStartTime = slotTimes[maxSlotUsed] + matchDur;
      } else if (pacingMode === 'one_gap') {
        // ONE_GAP — THIRDS STRUCTURE (fixed 6 rotations)
        //
        // A phase always has R1 + R2 for all pairs. The number of R1 matches
        // ALWAYS divides evenly by 3 (= number of groups, since 12 players/group
        // and courts = groups in the minimum setup). We therefore split the phase's
        // pairs into 3 equal parts P1, P2, P3 and run them in 6 rotations:
        //
        //   rot1: P1-R1   rot2: P2-R1   rot3: P1-R2  (P1 done)
        //   rot4: P3-R1   rot5: P2-R2   (P2 done)  rot6: P3-R2 (P3 done)
        //
        // Linkage: rot1→rot3, rot2→rot5, rot4→rot6 — each part plays R1 and
        // R2 with at least one rotation in between (no back-to-back). Each part
        // fills a whole rotation, so the courts are always full. P1 (lowest
        // groups under best_last) finishes first → the next phase can be drawn.

        // All pairs in the phase, in group order (weakest first under best_last)
        const allPairs = [];
        for (const k of sortedKeys) {
          for (const p of pairs.filter(
            (pp) => (pp.r1?.primaryGroup || pp.r1?.groupContext) === k,
          )) {
            allPairs.push(p);
          }
        }
        // Fallback if the primaryGroup filter didn't catch everything
        if (allPairs.length !== pairs.length) {
          allPairs.length = 0;
          allPairs.push(...pairs);
        }

        // Split the phase's pairs into N equal parts. N depends on the court count:
        //   Standard (courts = groups): N = 3 → 6 rotations
        //   Spacious (more courts):     N = 2 → 4 rotations
        // Each part-round fills about one rotation. N is chosen so the part size
        // ≈ courts. At least 2 parts (otherwise no gap is possible).
        //
        // Order (example N=3): P1R1, P2R1, P1R2, P3R1, P2R2, P3R2.
        // In general: lay out R1 for parts 1..N, but interleave R2 for part i as
        // soon as ≥1 part has passed since its R1 — this gives the pattern where
        // each part has ANOTHER part between its own R1 and R2 (a real gap).
        // Split the phase's pairs into N equal parts. N depends on the court count.
        const numParts = Math.max(2, Math.round(allPairs.length / Math.max(1, courts)));
        const partSize = Math.ceil(allPairs.length / numParts);
        const parts = [];
        for (let i = 0; i < numParts; i++) {
          parts.push(allPairs.slice(i * partSize, (i + 1) * partSize));
        }

        // Build the step order: interleave R1 and R2 so each part gets ≥1 part
        // in between. Buffer rule: hold back an R2 so the last part also gets a gap.
        const rotationPlan = [];
        const r1Placed = [];
        const r2Done = new Array(numParts).fill(false);
        let nextR1 = 0;
        while (rotationPlan.length < numParts * 2) {
          let r2c = -1;
          for (let s = 0; s < numParts; s++) {
            if (r2Done[s] || r1Placed[s] === undefined) continue;
            if (rotationPlan.length - r1Placed[s] >= 2) {
              r2c = s;
              break;
            }
          }
          const hasR1 = nextR1 < numParts;
          const r2Pending = r2Done.reduce(
            (a, d, s) => a + (!d && r1Placed[s] !== undefined ? 1 : 0),
            0,
          );
          let placeR1;
          if (!hasR1) placeR1 = false;
          else if (r2c === -1) placeR1 = true;
          else if (r2Pending < 2) placeR1 = true;
          else placeR1 = false;

          if (placeR1) {
            rotationPlan.push({ part: parts[nextR1], round: 1 });
            r1Placed[nextR1] = rotationPlan.length - 1;
            nextR1++;
          } else {
            if (r2c === -1) {
              for (let s = 0; s < numParts; s++) {
                if (!r2Done[s] && r1Placed[s] !== undefined) {
                  r2c = s;
                  break;
                }
              }
            }
            rotationPlan.push({ part: parts[r2c], round: 2 });
            r2Done[r2c] = true;
          }
        }

        // Build a FLAT match stream in step order, and pack it
        // courts-at-a-time.
        //
        // At minimum courts (courts = groups) this gives exactly 6
        // rotations — one per step — with a gap of 2 between R1 and R2.
        //
        // With MORE courts (Spacious/Efficient) the matches are packed more tightly:
        // several steps flow into the same rotation, so the tournament gets FEWER
        // rotations and becomes shorter. The gap order in the stream
        // (P1R1...P1R2 with P2R1 in between) ensures that R1 and R2 of the same pair
        // still end up in different rotations — no back-to-back — as long as
        // the court count is not so high that everything is pressed into 2 rotations
        // ("All at once", where a gap is impossible by definition).
        const matchStream = [];
        rotationPlan.forEach(({ part, round }) => {
          part.forEach((pair) => {
            const m = round === 1 ? pair.r1 : pair.r2;
            if (m) matchStream.push(m);
          });
        });

        // Distribute the matches EVENLY across the rotations instead of filling
        // each rotation completely. If you fill up, the last rotation often ends up
        // heavily understaffed (e.g. [17,17,17,15]) — and the pairs that end up there
        // lose their gap. An even split ([17,16,17,16]) gives all rotations almost
        // equal size, so the gap structure holds.
        //
        // The extra matches are placed on EVERY OTHER rotation (even index
        // first), so the large and small alternate — this gives a 17,16,17,16 pattern
        // rather than 17,17,16,16.
        const totalMatches = matchStream.length;
        const numRot = Math.max(1, Math.ceil(totalMatches / courts));
        const baseSize = Math.floor(totalMatches / numRot);
        let extraRot = totalMatches - baseSize * numRot; // number of +1 rotations
        const rotSizes = [];
        for (let i = 0; i < numRot; i++) {
          if (extraRot > 0 && i % 2 === 0) {
            rotSizes.push(baseSize + 1);
            extraRot--;
          } else {
            rotSizes.push(baseSize);
          }
        }
        // If there are still extras left (more extras than even-index slots),
        // fill the remaining rotations.
        let fillIdx = 0;
        while (extraRot > 0) {
          if (rotSizes[fillIdx] === baseSize) {
            rotSizes[fillIdx] = baseSize + 1;
            extraRot--;
          }
          fillIdx = (fillIdx + 1) % numRot;
        }

        const rotations = [];
        let streamPos = 0;
        rotSizes.forEach((size) => {
          rotations.push(matchStream.slice(streamPos, streamPos + size));
          streamPos += size;
        });

        // Assign times: rotation i → slotTimes[i], court = position in the rotation.
        const totalSlots = rotations.length;
        const slotTimes = [];
        let t = phaseStartTime;
        for (let i = 0; i < totalSlots; i++) {
          t = skipBreaks(t);
          slotTimes.push(t);
          t += slotMin;
        }

        let maxSlotUsed = -1;
        rotations.forEach((rot, slot) => {
          rot.forEach((m, courtIdx) => {
            if (!m) return;
            m.court = courtIdx + 1;
            m.timeMinutes = slotTimes[slot];
            m.time = minutesToTime(slotTimes[slot]);
            maxSlotUsed = Math.max(maxSlotUsed, slot);
          });
        });

        phaseStartTime = (maxSlotUsed >= 0 ? slotTimes[maxSlotUsed] : phaseStartTime) + matchDur;
      } else if (pacingMode === 'packed') {
        // PACKED — for Efficient and All at once.
        //
        // Here there is no gap choice to make: with many courts the phase is played
        // in few rotations. We ignore the R1/R2 order entirely and just pack
        // ALL the phase's matches (R1 + R2 for all pairs) as tightly and evenly as
        // possible across the rotations. No small "remainder" rotations — the matches
        // are distributed evenly (e.g. [8,8,7,7] rather than [10,10,5,5]).
        const allMatchesPhase = [];
        for (const p of pairs) {
          if (p.r1) allMatchesPhase.push(p.r1);
        }
        for (const p of pairs) {
          if (p.r2) allMatchesPhase.push(p.r2);
        }

        const totalMatches = allMatchesPhase.length;
        const numRot = Math.max(1, Math.ceil(totalMatches / courts));
        const baseSize = Math.floor(totalMatches / numRot);
        let extraRot = totalMatches - baseSize * numRot;
        const rotSizes = [];
        for (let i = 0; i < numRot; i++) {
          if (extraRot > 0 && i % 2 === 0) {
            rotSizes.push(baseSize + 1);
            extraRot--;
          } else {
            rotSizes.push(baseSize);
          }
        }
        let fillIdx = 0;
        while (extraRot > 0) {
          if (rotSizes[fillIdx] === baseSize) {
            rotSizes[fillIdx] = baseSize + 1;
            extraRot--;
          }
          fillIdx = (fillIdx + 1) % numRot;
        }

        const rotations = [];
        let streamPos = 0;
        rotSizes.forEach((size) => {
          rotations.push(allMatchesPhase.slice(streamPos, streamPos + size));
          streamPos += size;
        });

        const totalSlots = rotations.length;
        const slotTimes = [];
        let t = phaseStartTime;
        for (let i = 0; i < totalSlots; i++) {
          t = skipBreaks(t);
          slotTimes.push(t);
          t += slotMin;
        }

        let maxSlotUsed = -1;
        rotations.forEach((rot, slot) => {
          rot.forEach((m, courtIdx) => {
            if (!m) return;
            m.court = courtIdx + 1;
            m.timeMinutes = slotTimes[slot];
            m.time = minutesToTime(slotTimes[slot]);
            maxSlotUsed = Math.max(maxSlotUsed, slot);
          });
        });

        phaseStartTime = (maxSlotUsed >= 0 ? slotTimes[maxSlotUsed] : phaseStartTime) + matchDur;
      }

      // CHECK FOR an after_phase BREAK: if the user has configured a
      // break with afterPhase === currentPhase, insert it now. The break
      // starts right after the last match in this phase is finished (no dead time).
      const afterPhaseBreak = phaseBreaks.find((b) => b.afterPhase === phase);
      if (afterPhaseBreak) {
        // phaseStartTime is now at "right after the last match end". The break
        // starts here and lasts durationMin minutes.
        phaseStartTime += afterPhaseBreak.durationMin;
      }
    }
  }
}

// ------------------------------------------------------------
// RESOLVE PLACEHOLDERS
//
// After each phase: go through all incomplete matches and resolve
// placeholder slots to concrete players based on groupRankings
// ------------------------------------------------------------
export function resolvePlaceholders(matches, groupRankings) {
  // groupRankings = { phase: { groupLetter: [player1, player2, player3, ...] } }
  return matches.map((m) => {
    if (m.completed) return m;

    const resolveSlot = (slot) => {
      if (slot.kind === 'concrete') return slot;
      // Look up in the ranking
      const phaseRanks = groupRankings[slot.fromPhase];
      if (!phaseRanks) return slot;
      const groupRanks = phaseRanks[slot.fromGroup];
      if (!groupRanks) return slot;
      const player = groupRanks[slot.fromPosition - 1];
      if (!player) return slot;
      return {
        kind: 'concrete',
        playerId: player.id,
        originGroup: slot.fromGroup,
        // Store the phase version so we can show "Grp A2" in the live view.
        // `labelPhase` when the slot carries one — the finals rank on the last
        // phase and are played in that phase's groups, so `+1` would stamp a
        // phase that never existed. Ranked in phase 1 → the player is in phase
        // 2's group, which is what the fallback still says.
        originPhase: slot.labelPhase ?? slot.fromPhase + 1,
        originalPlaceholder: slot.label,
        // Lucky Looser marking: slot.luckyHint is set by generateFinalsStructure
        // when this finals slot fetches a player via the lucky looser path.
        // Carried over to the concrete slot so the finals view can show it.
        ...(slot.luckyHint
          ? { luckyMove: slot.luckyHint.move, luckyFrom: slot.luckyHint.fromGroup }
          : {}),
      };
    };

    return {
      ...m,
      team1: { p1: resolveSlot(m.team1.p1), p2: resolveSlot(m.team1.p2) },
      team2: { p1: resolveSlot(m.team2.p1), p2: resolveSlot(m.team2.p2) },
    };
  });
}

// ------------------------------------------------------------
// STANDINGS
// ------------------------------------------------------------
export function initStandings(players) {
  const s = {};
  players.forEach((p) => {
    s[p.id] = { points: 0, gamesWon: 0, gamesLost: 0, matchesPlayed: 0, wins: 0 };
  });
  return s;
}

// ------------------------------------------------------------
// RANKING HIERARCHY (applies to promotion/demotion and final placement):
//
// DEFAULT order:
//  1. NUMBER OF WINS (most first)
//  2. GAMES DIFFERENCE (gamesWon - gamesLost) — like goal difference in football
//  3. HEAD-TO-HEAD — if they have met, the winner ahead
//  4. NUMBER OF GAMES WON (most first)
//
// The order can be changed per tournament via the rankingOrder config
// (drag-and-drop in the wizard). Each criterion can also be deactivated.
//
// ELO does NOT affect ranking — only the start seeding at tournament start.
// Bonus points (phaseBonuses) affect ONLY the final ranking across groups,
// not promotion/demotion within a group.
// ------------------------------------------------------------

// Default ranking for Padel24 (all on, standard order).
// Used if the tournament has no rankingOrder config of its own (old
// tournaments or if the user hasn't adjusted anything).
export const DEFAULT_PADEL24_RANKING_ORDER = [
  { id: 'wins', enabled: true },
  { id: 'gamesDiff', enabled: true },
  { id: 'h2h', enabled: true },
  { id: 'gamesWon', enabled: true },
  { id: 'oppStrength', enabled: true },
];

/**
 * The criterion appended to any stored order that predates it.
 *
 * A tournament created before `oppStrength` existed carries a four-item
 * `rankingOrder`, and the configured order is used INSTEAD of the default — so
 * without this those tournaments would keep falling straight through to the
 * draw. It is appended last and enabled, which is where the owner asked for it;
 * turning it off is a drag away in the wizard.
 */
const APPENDED_CRITERIA = ['oppStrength'];

/** A completed GROUP match — the set every ranking criterion reads. */
function isRankableMatch(m) {
  return m.completed && !m.isFinal && m.phase !== 'finals';
}

/**
 * The phase the standings being ranked belong to.
 *
 * DERIVED FROM THE MATCHES rather than passed in, and that is deliberate.
 * `resetStandingsForPhase` wipes wins/games every phase, so the table is about
 * ONE phase — but `rankGroups` is handed the whole match list and no phase.
 * Threading a phase argument through `rankGroups`, `compareForRanking`,
 * `applyPromotionDemotion` and `applyBlockPromotionDemotion` would work only if
 * every call site passed it, and a call site that forgot would silently fall
 * back to the whole tournament. That is the exact failure this criterion exists
 * to remove: the finals are seeded from a ranking frozen when a group finishes,
 * and the table is ranked live afterwards. If those two disagree about which
 * matches count, they disagree about the order — which is how a group table can
 * show one player 4th while the other took the finals place.
 *
 * The highest completed group phase is the same number in both readings,
 * because every earlier phase is finished by then.
 */
function currentPhaseOf(matches) {
  let phase = null;
  for (const m of matches) {
    if (!isRankableMatch(m)) continue;
    if (typeof m.phase !== 'number') continue;
    if (phase === null || m.phase > phase) phase = m.phase;
  }
  return phase;
}

/**
 * How strong the opposition a player faced in `phase` was, as an average of the
 * OPPOSING team's ELO before each match.
 *
 * `teamElo1Pre` / `teamElo2Pre` are stamped on the match when the result is
 * recorded, so this reads history rather than today's ratings: it cannot move
 * after the fact, which is what makes it safe as a tie-break. Returns null when
 * there is nothing to measure — old matches predate those fields.
 */
function averageOpponentElo(playerId, matches, phase) {
  let sum = 0;
  let n = 0;
  for (const m of matches) {
    if (!isRankableMatch(m)) continue;
    if (phase !== null && m.phase !== phase) continue;
    const onT1 = [m.team1?.p1, m.team1?.p2].some(
      (s) => s?.kind === 'concrete' && s.playerId === playerId,
    );
    const onT2 = [m.team2?.p1, m.team2?.p2].some(
      (s) => s?.kind === 'concrete' && s.playerId === playerId,
    );
    if (!onT1 && !onT2) continue;
    const oppElo = onT1 ? m.teamElo2Pre : m.teamElo1Pre;
    if (typeof oppElo !== 'number') continue;
    sum += oppElo;
    n++;
  }
  return n === 0 ? null : sum / n;
}

// Helper function: compare two players by one criterion.
// Returns < 0 if a comes BEFORE b (better), > 0 if b comes before a, 0 = equal.
export function compareByCriterion(criterion, a, b, sA, sB, matches) {
  if (criterion === 'oppStrength') {
    // TOUGHEST OPPOSITION WINS THE TIE (product decision, 2026-09-30).
    //
    // Everything above this measures what a player did with the draw they got.
    // Two players can be level on all of it and have played completely
    // different opposition — «alt annet likt, så vinner den som har hatt
    // tøffest motstand».
    //
    // Scoped to the CURRENT PHASE, because the standings it breaks a tie in are
    // the current phase's. Returns 0 when either player has no measurable
    // opposition, so the comparison falls through rather than inventing an
    // order from missing data.
    if (!matches || !matches.length) return 0;
    const phase = currentPhaseOf(matches);
    const eloA = averageOpponentElo(a.id, matches, phase);
    const eloB = averageOpponentElo(b.id, matches, phase);
    if (eloA === null || eloB === null) return 0;
    return eloB - eloA;
  }
  if (criterion === 'wins') {
    // Draws count half (#568): one win + one draw ranks ahead of one win + one loss.
    const creditA = (sA.wins || 0) + 0.5 * (sA.draws || 0);
    const creditB = (sB.wins || 0) + 0.5 * (sB.draws || 0);
    return creditB - creditA;
  }
  if (criterion === 'gamesDiff') {
    const diffA = (sA.gamesWon || 0) - (sA.gamesLost || 0);
    const diffB = (sB.gamesWon || 0) - (sB.gamesLost || 0);
    return diffB - diffA;
  }
  if (criterion === 'gamesWon') {
    return (sB.gamesWon || 0) - (sA.gamesWon || 0);
  }
  if (criterion === 'h2h' && matches && matches.length) {
    const headToHead = matches.find((m) => {
      if (!m.completed) return false;
      // A FINAL IS NOT A GROUP MATCH — the same rule the standings follow, and
      // this is the other place match data reaches the ranking.
      //
      // The finals are SEEDED from `groupRankings[lastPhase]`, frozen when a
      // group finishes its last phase — when no final is completed, so this
      // scan saw group matches only. The table on screen is ranked live, after
      // the finals are played, when it could see them. The same group then came
      // out in two different orders from the same function, and the screen
      // contradicted the draw it had produced: «lucky looser is the 2nd in the
      // group below, not the 1st».
      //
      // Sorting is PAIRWISE, so the two players being compared need not have
      // met each other — one flipped comparison anywhere in the group is enough
      // to move the order.
      if (m.isFinal || m.phase === 'finals') return false;
      const t1Players = [m.team1.p1, m.team1.p2]
        .filter((s) => s.kind === 'concrete')
        .map((s) => s.playerId);
      const t2Players = [m.team2.p1, m.team2.p2]
        .filter((s) => s.kind === 'concrete')
        .map((s) => s.playerId);
      const aOnT1 = t1Players.includes(a.id);
      const aOnT2 = t2Players.includes(a.id);
      const bOnT1 = t1Players.includes(b.id);
      const bOnT2 = t2Players.includes(b.id);
      // Head-to-head means they played against each other (not as partners)
      return (aOnT1 && bOnT2) || (aOnT2 && bOnT1);
    });
    if (headToHead && headToHead.score1 !== headToHead.score2) {
      const aOnT1 = [headToHead.team1.p1, headToHead.team1.p2].some(
        (s) => s.kind === 'concrete' && s.playerId === a.id,
      );
      const aWon = aOnT1
        ? headToHead.score1 > headToHead.score2
        : headToHead.score2 > headToHead.score1;
      return aWon ? -1 : 1;
    }
    return 0;
  }
  return 0;
}

export function compareForRanking(a, b, standings, matches, rankingOrder = null) {
  const sA = standings[a.id] || {};
  const sB = standings[b.id] || {};

  // Use the tournament's rankingOrder, or fall back to the Padel24 default.
  // Ensures we always have a valid order even for old tournaments.
  const order =
    Array.isArray(rankingOrder) && rankingOrder.length > 0
      ? rankingOrder
      : DEFAULT_PADEL24_RANKING_ORDER;

  // Go through each enabled criterion in order. The first one that
  // returns ≠ 0 determines the ranking.
  for (const item of order) {
    if (!item.enabled) continue;
    const cmp = compareByCriterion(item.id, a, b, sA, sB, matches);
    if (cmp !== 0) return cmp;
  }

  // Criteria the stored order predates — see APPENDED_CRITERIA.
  const configured = new Set(order.map((item) => item.id));
  for (const id of APPENDED_CRITERIA) {
    if (configured.has(id)) continue;
    const cmp = compareByCriterion(id, a, b, sA, sB, matches);
    if (cmp !== 0) return cmp;
  }

  // ──────────────────────────────────────────────────────────
  // THE DRAW — and it is DETERMINISTIC on purpose.
  //
  // This used to `return 0`, which left the pair in whatever order the input
  // array happened to hold. That is not merely arbitrary, it is UNSTABLE
  // BETWEEN CALLERS: the finals are seeded from a ranking frozen when a group
  // finishes its last phase, and the group table on screen is ranked again
  // afterwards from a different array. Two players dead level on every
  // criterion could therefore come out 4th on screen and take the finals place
  // — reported on a 6-court run, «Håkon og Jonas har nøyaktig samme poengsum,
  // men den som fikk plassen i finalen var Jonas».
  //
  // Comparing ids settles it the same way every time, for every caller, for
  // ever. It is a coin toss, not a sporting criterion — which is exactly what
  // the owner asked for once opposition strength has also failed to separate
  // them — and a coin toss that lands differently each time you look at it is
  // the bug, not the feature.
  // ──────────────────────────────────────────────────────────
  if (a.id === b.id) return 0;
  return String(a.id) < String(b.id) ? -1 : 1;
}

/**
 * WHICH criterion put `a` ahead of `b` — for showing a player why they are
 * where they are.
 *
 * Runs the same chain as `compareForRanking` and reports the first step that
 * separated them, so the explanation can never drift from the order: both read
 * the one implementation. Returns `'draw'` for the deterministic tie-break and
 * null when the two are genuinely the same player.
 */
export function explainRankingDecision(a, b, standings, matches, rankingOrder = null) {
  const sA = standings[a.id] || {};
  const sB = standings[b.id] || {};
  const order =
    Array.isArray(rankingOrder) && rankingOrder.length > 0
      ? rankingOrder
      : DEFAULT_PADEL24_RANKING_ORDER;

  for (const item of order) {
    if (!item.enabled) continue;
    const cmp = compareByCriterion(item.id, a, b, sA, sB, matches);
    if (cmp !== 0) return { criterion: item.id, aheadIsA: cmp < 0 };
  }
  const configured = new Set(order.map((item) => item.id));
  for (const id of APPENDED_CRITERIA) {
    if (configured.has(id)) continue;
    const cmp = compareByCriterion(id, a, b, sA, sB, matches);
    if (cmp !== 0) return { criterion: id, aheadIsA: cmp < 0 };
  }
  if (a.id === b.id) return null;
  return { criterion: 'draw', aheadIsA: String(a.id) < String(b.id) };
}

// ------------------------------------------------------------
// REPLACE PLAYER — swap out a player mid-tournament
//
// Use case: the player gets injured or has to withdraw, and a substitute takes over.
//
// Rules:
//   - The original player's ELO history AND results from played matches stay untouched.
//   - All FUTURE (not completed) matches get their playerId replaced with the substitute.
//   - The substitute is brought in with their current ELO and takes over the group membership.
//   - We mark this in a `replacements` array on the tournament for visual tracking.
//
// Returns the new tournament state.
// ------------------------------------------------------------
export function replacePlayerInTournament(tournament, originalId, replacementPlayer) {
  if (!tournament) return tournament;

  // Build the new matches list: only swap in matches that do NOT YET have a result
  const replacementTimestamp = Date.now();
  const completedCount = (tournament.matches || []).filter(
    (m) =>
      m.score1 !== null && m.score2 !== null && m.score1 !== undefined && m.score2 !== undefined,
  ).length;
  const totalCount = (tournament.matches || []).length;
  const currentPhase = tournament.currentPhase || tournament.phase || 1;
  const currentRound = tournament.currentRound || 1;

  // Compute the ORIGINAL player's completed matches at the swap time
  // (used to hide the substitute badge if the swap happened before the player got to play)
  const originalCompletedCount = (tournament.matches || []).filter((m) => {
    if (m.score1 == null || m.score2 == null) return false;
    const slots = [m.team1?.p1, m.team1?.p2, m.team2?.p1, m.team2?.p2];
    return slots.some((s) => s?.kind === 'concrete' && s.playerId === originalId);
  }).length;

  const newMatches = (tournament.matches || []).map((m) => {
    if (
      m.score1 !== null &&
      m.score2 !== null &&
      m.score1 !== undefined &&
      m.score2 !== undefined
    ) {
      return m; // already played — don't touch
    }
    const replaceSlot = (slot) => {
      if (slot.kind === 'concrete' && slot.playerId === originalId) {
        return {
          ...slot,
          playerId: replacementPlayer.id,
          isReplacement: true,
          replacedPlayerId: originalId,
          replacementTimestamp,
          replacementAfterMatch: completedCount,
          replacementTotalMatches: totalCount,
          replacementPhase: currentPhase,
          replacementRound: currentRound,
          originalCompletedCount, // how many matches the original got to play
        };
      }
      return slot;
    };
    return {
      ...m,
      team1: { p1: replaceSlot(m.team1.p1), p2: replaceSlot(m.team1.p2) },
      team2: { p1: replaceSlot(m.team2.p1), p2: replaceSlot(m.team2.p2) },
    };
  });

  // Update the player database in the tournament
  const replacementExisting = (tournament.players || []).find((p) => p.id === replacementPlayer.id);
  const newPlayers = [...(tournament.players || [])];
  if (!replacementExisting) {
    newPlayers.push({ ...replacementPlayer });
  }

  // Update groups — swap the reference in all groups
  const newGroups = (tournament.groups || []).map((g) => ({
    ...g,
    players: g.players.map((p) => (p.id === originalId ? { ...replacementPlayer } : p)),
  }));

  // Update standings — keep building points under the substitute's name from now on
  // standings is an OBJECT {playerId: {wins, points, ...}}, not an array
  // The original's points are kept (frozen at the point they were swapped out)
  // The substitute gets zeroed-out stats and collects points from now on
  const oldStandings = tournament.standings || {};
  const originalEntry = oldStandings[originalId] || {};
  const newStandings = { ...oldStandings };

  // The original keeps their points but is marked as swapped out
  if (oldStandings[originalId]) {
    newStandings[originalId] = { ...oldStandings[originalId], isReplacedOut: true };
  }

  // The substitute gets a fresh standings entry (with the original's group membership)
  if (!newStandings[replacementPlayer.id]) {
    newStandings[replacementPlayer.id] = {
      wins: 0,
      losses: 0,
      draws: 0,
      gamesWon: 0,
      gamesLost: 0,
      matchesPlayed: 0,
      points: 0,
      phaseBonuses: 0,
      isReplacement: true,
      replacedPlayerId: originalId,
      groupLetter: originalEntry.groupLetter,
    };
  }

  // Find the FIRST match the substitute is put into — this is the context for the swap
  const firstReplacementMatch = newMatches.find((m) => {
    const slots = [m.team1.p1, m.team1.p2, m.team2.p1, m.team2.p2];
    return slots.some(
      (s) =>
        s.isReplacement && s.playerId === replacementPlayer.id && s.replacedPlayerId === originalId,
    );
  });

  // Compute the match number (1-indexed position in the chronological match list)
  const sortedAllMatches = [...newMatches].sort((a, b) => {
    const da = a.date || '',
      db = b.date || '';
    if (da !== db) return da < db ? -1 : 1;
    return (a.timeMinutes ?? 9999) - (b.timeMinutes ?? 9999);
  });
  const matchNumber = firstReplacementMatch
    ? sortedAllMatches.findIndex((m) => m.id === firstReplacementMatch.id) + 1
    : null;

  // Store the match info so the popup can show it without an extra lookup
  const firstMatchInfo = firstReplacementMatch
    ? {
        id: firstReplacementMatch.id,
        matchNumber,
        phase: firstReplacementMatch.phase,
        round: firstReplacementMatch.round,
        court: firstReplacementMatch.court,
        time: firstReplacementMatch.time,
        date: firstReplacementMatch.date,
        team1Names: [firstReplacementMatch.team1.p1, firstReplacementMatch.team1.p2]
          .filter((s) => s.kind === 'concrete')
          .map((s) => {
            const pid = s.playerId;
            const pl =
              pid === replacementPlayer.id
                ? replacementPlayer
                : (tournament.players || []).find((p) => p.id === pid);
            return pl?.name || '?';
          })
          .join(' + '),
        team2Names: [firstReplacementMatch.team2.p1, firstReplacementMatch.team2.p2]
          .filter((s) => s.kind === 'concrete')
          .map((s) => {
            const pid = s.playerId;
            const pl =
              pid === replacementPlayer.id
                ? replacementPlayer
                : (tournament.players || []).find((p) => p.id === pid);
            return pl?.name || '?';
          })
          .join(' + '),
      }
    : null;

  // Attach firstMatchInfo to all swap slots
  const enrichedMatches = newMatches.map((m) => {
    const enrichSlot = (slot) => {
      if (
        slot.isReplacement &&
        slot.replacedPlayerId === originalId &&
        slot.replacementTimestamp === replacementTimestamp
      ) {
        return { ...slot, firstMatchAsReplacement: firstMatchInfo };
      }
      return slot;
    };
    return {
      ...m,
      team1: { p1: enrichSlot(m.team1.p1), p2: enrichSlot(m.team1.p2) },
      team2: { p1: enrichSlot(m.team2.p1), p2: enrichSlot(m.team2.p2) },
    };
  });

  // Log entry for the swap.
  // THE LOG STORES WHAT HAPPENED, NOT A SENTENCE (#519). The words are the
  // catalog's (`tournament.log.event.*`, apps/web), decided by whoever reads
  // the log. A missing name is a neutral «?» rather than a Norwegian fallback
  // word, and ELO is a string so it renders exactly as before, ungrouped.
  // `currentRound` is `tournament.currentRound || 1`, so it is always present.
  const originalPlayer = (tournament.players || []).find((p) => p.id === originalId);
  const logItem = {
    time: new Date().toISOString(),
    code: 'substitution',
    vars: {
      original: originalPlayer?.name || '?',
      replacement: replacementPlayer.name,
      eloFrom: String(originalPlayer?.elo || '?'),
      eloTo: String(replacementPlayer.elo),
      phase: currentPhase,
      round: currentRound,
      completed: completedCount,
      total: totalCount,
    },
  };

  // Log the swap
  const replacement = {
    id: `repl_${replacementTimestamp}`,
    originalId,
    replacementId: replacementPlayer.id,
    timestamp: replacementTimestamp,
    afterMatchCount: completedCount,
    firstMatchInfo,
  };

  return {
    ...tournament,
    matches: enrichedMatches,
    players: newPlayers,
    groups: newGroups,
    standings: newStandings,
    replacements: [...(tournament.replacements || []), replacement],
    log: [...(tournament.log || []), logItem],
  };
}

// ------------------------------------------------------------
// FILL PLACEHOLDER — swap a reserved place for a real player
//
// Unlike replacePlayerInTournament, we don't keep any
// replacement history: it's as if the new player has always
// been in that place. Used to fill in
// "Reservert plass N" placeholders after the tournament has started.
// ------------------------------------------------------------
export function fillPlaceholderInTournament(tournament, placeholderId, newPlayer) {
  if (!tournament) return tournament;

  // Replace all slot references from placeholderId to newPlayer.id
  const replaceSlot = (slot) => {
    if (slot.kind === 'concrete' && slot.playerId === placeholderId) {
      return { ...slot, playerId: newPlayer.id };
    }
    return slot;
  };

  const newMatches = (tournament.matches || []).map((m) => ({
    ...m,
    team1: { p1: replaceSlot(m.team1.p1), p2: replaceSlot(m.team1.p2) },
    team2: { p1: replaceSlot(m.team2.p1), p2: replaceSlot(m.team2.p2) },
  }));

  // Swap the placeholder for the new player in the players array
  const placeholderRef = (tournament.players || []).find((p) => p.id === placeholderId);
  const newPlayers = (tournament.players || []).map((p) =>
    p.id === placeholderId ? { ...newPlayer } : p,
  );

  // Swap in the groups
  const newGroups = (tournament.groups || []).map((g) => ({
    ...g,
    players: g.players.map((p) => (p.id === placeholderId ? { ...newPlayer } : p)),
  }));

  // Swap the key in standings (transfer all accumulated points/stats)
  const newStandings = { ...(tournament.standings || {}) };
  if (newStandings[placeholderId]) {
    newStandings[newPlayer.id] = newStandings[placeholderId];
    delete newStandings[placeholderId];
  }

  // Data, not a sentence (#519): see replacePlayerInTournament.
  const logItem = {
    time: new Date().toISOString(),
    code: 'placeholderFilled',
    vars: {
      placeholder: placeholderRef?.name || '?',
      player: newPlayer.name,
      elo: String(newPlayer.elo),
    },
  };

  return {
    ...tournament,
    matches: newMatches,
    players: newPlayers,
    groups: newGroups,
    standings: newStandings,
    log: [...(tournament.log || []), logItem],
  };
}

// ------------------------------------------------------------
// RESET STANDINGS — at phase start
//
// Each phase counts separately. When a new phase starts:
//   - wins, gamesWon, gamesLost, matchesPlayed, points → 0
//   - phaseBonuses are kept (accumulated over the whole tournament)
//
// So that each phase has its own ranking based only on the matches
// played in that phase. But bonus points for winning groups accumulate across phases.
// ------------------------------------------------------------
export function resetStandingsForPhase(standings, players) {
  const reset = {};
  players.forEach((p) => {
    const cur = standings[p.id] || {};
    reset[p.id] = {
      points: 0,
      gamesWon: 0,
      gamesLost: 0,
      matchesPlayed: 0,
      wins: 0,
      phaseBonuses: cur.phaseBonuses || 0, // kept
    };
  });
  return reset;
}

export function applyMatchResult(standings, match, score1, score2) {
  const newS = { ...standings };
  const allSlots = [
    ...[match.team1.p1, match.team1.p2].map((s) => ({ slot: s, won: score1, lost: score2 })),
    ...[match.team2.p1, match.team2.p2].map((s) => ({ slot: s, won: score2, lost: score1 })),
  ];
  allSlots.forEach(({ slot, won, lost }) => {
    if (slot.kind !== 'concrete') return;
    const cur = newS[slot.playerId] || {
      points: 0,
      gamesWon: 0,
      gamesLost: 0,
      matchesPlayed: 0,
      wins: 0,
      draws: 0,
      phaseBonuses: 0,
    };
    newS[slot.playerId] = {
      ...cur,
      points: cur.points + won,
      gamesWon: cur.gamesWon + won,
      gamesLost: cur.gamesLost + lost,
      matchesPlayed: cur.matchesPlayed + 1,
      wins: cur.wins + (won > lost ? 1 : 0),
      // A draw is half a win on the «wins» criterion (#568). Entries written
      // before this field existed read as 0 draws, so only draws recorded from
      // now on earn the credit — the table's U column counts from the matches
      // and is unaffected.
      draws: (cur.draws || 0) + (won === lost ? 1 : 0),
    };
  });
  return newS;
}

// ------------------------------------------------------------
// RANK GROUPS AFTER A PHASE
//
// Returns { 'A': [topplayer, ...], 'B': [...] }
// based on results (wins → games diff → head-to-head), NOT ELO or bonus points
// ------------------------------------------------------------
export function rankGroups(groups, standings, matches = [], rankingOrder = null) {
  const ranked = {};
  groups.forEach((g) => {
    ranked[g.letter] = [...g.players].sort((a, b) =>
      compareForRanking(a, b, standings, matches, rankingOrder),
    );
  });
  return ranked;
}

// ------------------------------------------------------------
// PHASE BONUS — reward for winning your group (ONLY for the final ranking)
//   1st place: +3, 2nd place: +2, 3rd place: +1
//
// The bonuses do NOT affect promotion/demotion, only the final ranking.
// ------------------------------------------------------------
export const PHASE_BONUSES = [3, 2, 1]; // 1st, 2nd, 3rd place

export function awardPhaseBonuses(standings, groups) {
  const newStandings = { ...standings };

  groups.forEach((group) => {
    const sorted = [...group.players].sort(
      (a, b) => (standings[b.id]?.points || 0) - (standings[a.id]?.points || 0),
    );

    PHASE_BONUSES.forEach((bonus, idx) => {
      if (idx >= sorted.length) return;
      const p = sorted[idx];
      const cur = newStandings[p.id] || {
        points: 0,
        gamesWon: 0,
        gamesLost: 0,
        matchesPlayed: 0,
        wins: 0,
        phaseBonuses: 0,
      };
      newStandings[p.id] = {
        ...cur,
        points: cur.points + bonus,
        phaseBonuses: (cur.phaseBonuses || 0) + bonus,
      };
    });
  });

  return newStandings;
}

// ------------------------------------------------------------
// PROMOTION/DEMOTION — promotion/relegation between groups
//
// After each phase: the top N from group i+1 swap with the bottom N from group i.
//
// The positions in the new group are determined as follows:
//   UPPER GROUP (after promotion):
//     Pos 1 to (size-N): original players who stay, in their previous ranking
//     Pos (size-N+1) to size: promoted players (the winner of the lower group gets
//                               the best of these positions)
//
//   LOWER GROUP (after demotion):
//     Pos 1 to N: those demoted from the upper, best first (10th place in the upper = pos 1)
//     Pos N+1 to size: original lower players who stay, in their previous ranking
//
// This way the winner of a group keeps their position, while the winner of the lower
// group gets the top of the newcomers in the upper group.
// ------------------------------------------------------------
// ------------------------------------------------------------
// fixPlayerBackToBackConflicts — post-process that detects players
// who have back-to-back matches in phase 1 (after the draw) and swaps slot times
// with parallel matches to give the player a one-match break.
//
// Background: applyPacingMode packs R1[i]+R2[i] as a "pair" with one_gap
// in between. But in Padel24's mexicano-style draw, players change partners
// between R1 and R2 — a player can therefore be in R1 of pair 0 (slot 0)
// AND R2 of pair 1 (slot 1) → back-to-back.
//
// The algorithm:
//   1) For each player, sort their matches by time
//   2) For adjacent matches with gap < 2*minGap → conflict
//   3) Find a candidate match in the intervening slot (m1+minGap) that does not
//      involve the player, and where the players in the candidate are not in m2
//   4) Swap time+court between m2 and the candidate
//
// Max 50 swaps to avoid an endless loop.
// ------------------------------------------------------------
export function fixPlayerBackToBackConflicts(matches, config, phaseFilter = null) {
  const buffer = config.betweenMatches || 5;
  const dayConfigs = config.days || [];
  if (dayConfigs.length === 0) return matches;

  const matchDur = dayConfigs[0].matchDuration || 30;
  const minGap = matchDur + buffer; // time between the end of match N and the start of match N+1

  const getPlayerIds = (m) => {
    const ids = [];
    [m.team1?.p1, m.team1?.p2, m.team2?.p1, m.team2?.p2].forEach((s) => {
      if (s?.kind === 'concrete' && s.playerId) ids.push(s.playerId);
    });
    return ids;
  };

  // Relevant matches: in the phase we are considering, with a time set
  const relevant = matches.filter(
    (m) => m.timeMinutes !== undefined && (phaseFilter === null || m.phase === phaseFilter),
  );

  // Count the conflict score for a given time placement.
  // Overlap (two matches at the same time for the same player) is a HARD error → weight 1000.
  // Back-to-back (gap < 2*minGap) is a soft error → weight 1.
  // We minimize the total score; overlap must always be prioritized away.
  const conflictScore = (timeOf) => {
    const playerMatches = new Map();
    relevant.forEach((m) => {
      const t = timeOf(m);
      getPlayerIds(m).forEach((pid) => {
        if (!playerMatches.has(pid)) playerMatches.set(pid, []);
        playerMatches.get(pid).push(t);
      });
    });
    let score = 0;
    for (const times of playerMatches.values()) {
      if (times.length < 2) continue;
      times.sort((a, b) => a - b);
      for (let i = 0; i < times.length - 1; i++) {
        const gap = times[i + 1] - times[i];
        if (gap < minGap)
          score += 1000; // overlap — hard error
        else if (gap < 2 * minGap) score += 1; // back-to-back — soft error
      }
    }
    return score;
  };

  // Iterative improvement: try pairwise swaps of (time, court) between two matches
  // in the same phase. Keep a swap only if it REDUCES the total number of conflicts.
  const baseTimeOf = (m) => m.timeMinutes;
  let currentConflicts = conflictScore(baseTimeOf);

  const maxPasses = 8;
  for (let pass = 0; pass < maxPasses && currentConflicts > 0; pass++) {
    let improvedThisPass = false;

    // Find all matches involved in a conflict
    for (let i = 0; i < relevant.length; i++) {
      if (currentConflicts === 0) break;
      const mA = relevant[i];

      for (let j = i + 1; j < relevant.length; j++) {
        const mB = relevant[j];
        if (mA.phase !== mB.phase) continue;
        if (mA.timeMinutes === mB.timeMinutes && mA.court === mB.court) continue;

        // IMPORTANT: swap ONLY matches in the same group (primaryGroup). Otherwise
        // the fixer can move an A-group match to an early slot and break
        // the groupPlayOrder order (best_last → A should play last).
        // IMPORTANT: swap ONLY matches in the same group (primaryGroup) AND the same
        // round (R1↔R1, R2↔R2). Otherwise the fixer can move an R1 match to an
        // R2 time and break the rotation structure (R1 rotations vs R2 rotations).
        if ((mA.primaryGroup || mA.groupContext) !== (mB.primaryGroup || mB.groupContext)) continue;
        if (mA.round !== mB.round) continue;

        // Hypothetical swap: mA and mB swap time+court.
        const swappedTimeOf = (m) => {
          if (m === mA) return mB.timeMinutes;
          if (m === mB) return mA.timeMinutes;
          return m.timeMinutes;
        };
        const afterConflicts = conflictScore(swappedTimeOf);

        if (afterConflicts < currentConflicts) {
          // Keep the swap
          const tT = mA.timeMinutes,
            tS = mA.time,
            tC = mA.court;
          mA.timeMinutes = mB.timeMinutes;
          mA.time = mB.time;
          mA.court = mB.court;
          mB.timeMinutes = tT;
          mB.time = tS;
          mB.court = tC;
          currentConflicts = afterConflicts;
          improvedThisPass = true;
          break;
        }
      }
    }

    if (!improvedThisPass) break; // no improvement possible — stop
  }

  return matches;
}

// ------------------------------------------------------------
// DRAW PHASE 1 — fill TBA matches with concrete players
//
// Takes matches that contain TBA slots and uses drawPair1RoundMatches
// for each pair (cross or inner) to draw concrete players.
// ------------------------------------------------------------
export function drawPhase1Matches(matches, groups, drawRules, players) {
  const rules = {
    mode: 'varied',
    avoidSamePartner: true,
    avoidSameOpponent: true,
    balanceHomeAway: true,
    balanceElo: true,
    ...(drawRules || {}),
  };

  // Group the phase 1 matches by pair
  const pairsMap = {};
  matches.forEach((m, idx) => {
    if (m.phase !== 1 || !m.needsDraw) return;
    if (!m.phase1Pair) return;
    const key = m.phase1Pair.groupLetters.join('+');
    if (!pairsMap[key])
      pairsMap[key] = { phase1Pair: m.phase1Pair, matchIndices: { 1: [], 2: [] } };
    pairsMap[key].matchIndices[m.round].push(idx);
  });

  const newMatches = [...matches];

  (Object.values(pairsMap) as any[]).forEach(({ phase1Pair, matchIndices }) => {
    // Build the pair object from the group letters
    let pairObj;
    if (phase1Pair.type === 'cross') {
      const gA = groups.find((g) => g.letter === phase1Pair.groupLetters[0]);
      const gB = groups.find((g) => g.letter === phase1Pair.groupLetters[1]);
      if (!gA || !gB) return;
      pairObj = { type: 'cross', groups: [gA, gB] };
    } else {
      const g = groups.find((g) => g.letter === phase1Pair.groupLetters[0]);
      if (!g) return;
      pairObj = { type: 'inner', group: g };
    }

    // Shared history across R1 and R2 in the same pair
    const history = {
      partnerHistory: {},
      opponentHistory: {},
      homeAwayCount: {},
    };

    // Draw R1
    const r1Drawn = drawPair1RoundMatches(pairObj, 1, history, players, rules);
    matchIndices[1].forEach((origIdx, i) => {
      if (i >= r1Drawn.length) return;
      const d = r1Drawn[i];
      newMatches[origIdx] = {
        ...newMatches[origIdx],
        team1: d.team1,
        team2: d.team2,
        needsDraw: false,
      };
    });

    // Draw R2 (with history from R1)
    const r2Drawn = drawPair1RoundMatches(pairObj, 2, history, players, rules);
    matchIndices[2].forEach((origIdx, i) => {
      if (i >= r2Drawn.length) return;
      const d = r2Drawn[i];
      newMatches[origIdx] = {
        ...newMatches[origIdx],
        team1: d.team1,
        team2: d.team2,
        needsDraw: false,
      };
    });
  });

  return newMatches;
}

// ------------------------------------------------------------
// SMART DRAW — assign TBA matches when a phase starts
//
// Rules (in priority order — can be toggled on/off via config.drawRules):
//   1. Avoid the same partner as before
//   2. Avoid the same opponent as before
//   3. Balance home/away matches
//   4. Even ELO matches (lowest priority)
//
// Uses a greedy algorithm — for each match it picks the 4 players and
// team split that minimizes the conflict score.
// ------------------------------------------------------------
export function drawPhaseMatches(
  matches,
  groups,
  phase,
  allPreviousMatches,
  players = [],
  drawRules = {},
) {
  const rules: any = {
    avoidSamePartner: true,
    avoidSameOpponent: true,
    balanceHomeAway: true,
    balanceElo: false,
    ...drawRules,
  };

  // Build history from earlier matches
  const partnerHistory: any = {};
  const opponentHistory: any = {};
  const homeAwayCount: any = {};
  const playerById = {};
  players.forEach((p) => (playerById[p.id] = p));

  allPreviousMatches.forEach((m) => {
    const t1 = [m.team1.p1, m.team1.p2].filter((s) => s.kind === 'concrete').map((s) => s.playerId);
    const t2 = [m.team2.p1, m.team2.p2].filter((s) => s.kind === 'concrete').map((s) => s.playerId);
    if (t1.length !== 2 || t2.length !== 2) return;

    [...t1, ...t2].forEach((id) => {
      if (!partnerHistory[id]) partnerHistory[id] = new Set();
      if (!opponentHistory[id]) opponentHistory[id] = new Set();
      if (!homeAwayCount[id]) homeAwayCount[id] = { home: 0, away: 0 };
    });
    partnerHistory[t1[0]].add(t1[1]);
    partnerHistory[t1[1]].add(t1[0]);
    partnerHistory[t2[0]].add(t2[1]);
    partnerHistory[t2[1]].add(t2[0]);
    t1.forEach((a) =>
      t2.forEach((b) => {
        opponentHistory[a].add(b);
        opponentHistory[b].add(a);
      }),
    );
    t1.forEach((id) => homeAwayCount[id].home++);
    t2.forEach((id) => homeAwayCount[id].away++);
  });

  // Score functions — weights are chosen so that a lower priority cannot overshadow a higher one
  const PARTNER_PENALTY = 1000; // prio 1
  const OPPONENT_PENALTY = 500; // prio 2
  const HOME_AWAY_PENALTY = 50; // prio 3
  const ELO_PENALTY = 1; // prio 4 (multiplied by ELO diff)
  const SPEIL_BONUS = -2500; // negative = reward when the R1 partner is the R2 opponent
  const ELO_THRESHOLD = 100;

  const isSpeilrotasjon = rules.mode === 'speilrotasjon';

  function pairScore(a, b, asHome) {
    let score = 0;
    if ((rules.avoidSamePartner || isSpeilrotasjon) && partnerHistory[a]?.has(b))
      score += PARTNER_PENALTY;
    if (rules.balanceHomeAway) {
      const aBalance = (homeAwayCount[a]?.home || 0) - (homeAwayCount[a]?.away || 0);
      const bBalance = (homeAwayCount[b]?.home || 0) - (homeAwayCount[b]?.away || 0);
      if (asHome) {
        score += HOME_AWAY_PENALTY * (Math.max(0, aBalance) + Math.max(0, bBalance));
      } else {
        score += HOME_AWAY_PENALTY * (Math.max(0, -aBalance) + Math.max(0, -bBalance));
      }
    }
    return score;
  }

  function matchupScore(team1Ids, team2Ids) {
    let score = 0;
    if (isSpeilrotasjon) {
      // Speilrotasjon: reward the round 1 partner (FROM THE SAME PHASE) being an opponent in round 2
      // — uses phasePartners (only this phase's partners), not partnerHistory (all earlier)
      team1Ids.forEach((a) => {
        const r1Partners = phasePartners[a];
        if (r1Partners) {
          team2Ids.forEach((b) => {
            if (r1Partners.has(b)) score += SPEIL_BONUS;
          });
        }
      });
    }
    // avoidSameOpponent applies REGARDLESS of mode — it governs history from earlier phases
    if (rules.avoidSameOpponent) {
      team1Ids.forEach((a) =>
        team2Ids.forEach((b) => {
          if (opponentHistory[a]?.has(b)) score += OPPONENT_PENALTY;
        }),
      );
    }
    if (rules.balanceElo) {
      // Threshold-based: only penalize when the ELO diff is obviously unbalanced
      const t1p = team1Ids.map((id) => playerById[id]).filter(Boolean);
      const t2p = team2Ids.map((id) => playerById[id]).filter(Boolean);
      if (t1p.length === 2 && t2p.length === 2) {
        const t1Elo = (t1p[0].elo + t1p[1].elo) / 2;
        const t2Elo = (t2p[0].elo + t2p[1].elo) / 2;
        const diff = Math.abs(t1Elo - t2Elo);
        if (diff > ELO_THRESHOLD) {
          score += ELO_PENALTY * (diff - ELO_THRESHOLD);
        }
      }
    }
    return score;
  }

  const drawnByGroup = {};

  // Phase-internal partner tracking — only for the Speilrotasjon check in R2
  // (reset per group so that SPEIL_BONUS only applies to the R1 partner from this phase)
  const phasePartners = {};

  groups.forEach((g) => {
    const drawnMatches = [];

    // Reset phase-internal partner tracking for each group
    g.players.forEach((p) => {
      phasePartners[p.id] = new Set();
    });

    for (let round = 1; round <= 2; round++) {
      const shuffled = [...g.players].sort(() => Math.random() - 0.5);
      const roundMatches = [];

      while (shuffled.length >= 4) {
        let bestCombo = null;
        let bestScore = Infinity;

        const candidates = shuffled.slice(0, Math.min(12, shuffled.length));

        for (let i = 0; i < candidates.length; i++) {
          for (let j = i + 1; j < candidates.length; j++) {
            for (let k = j + 1; k < candidates.length; k++) {
              for (let l = k + 1; l < candidates.length; l++) {
                const four = [candidates[i], candidates[j], candidates[k], candidates[l]];

                const splits = [
                  [
                    [four[0], four[1]],
                    [four[2], four[3]],
                  ],
                  [
                    [four[0], four[2]],
                    [four[1], four[3]],
                  ],
                  [
                    [four[0], four[3]],
                    [four[1], four[2]],
                  ],
                ];

                splits.forEach((split) => {
                  const [t1, t2] = split;
                  const s =
                    pairScore(t1[0].id, t1[1].id, true) +
                    pairScore(t2[0].id, t2[1].id, false) +
                    matchupScore([t1[0].id, t1[1].id], [t2[0].id, t2[1].id]);
                  if (s < bestScore) {
                    bestScore = s;
                    bestCombo = { four, t1, t2 };
                  }
                });
              }
            }
          }
        }

        if (!bestCombo) break;

        roundMatches.push({
          team1: [bestCombo.t1[0], bestCombo.t1[1]],
          team2: [bestCombo.t2[0], bestCombo.t2[1]],
        });

        bestCombo.four.forEach((p) => {
          const idx = shuffled.findIndex((x) => x.id === p.id);
          if (idx >= 0) shuffled.splice(idx, 1);
        });
      }

      // Update history after the round
      roundMatches.forEach((rm) => {
        const t1Ids = rm.team1.map((p) => p.id);
        const t2Ids = rm.team2.map((p) => p.id);

        [...t1Ids, ...t2Ids].forEach((id) => {
          if (!partnerHistory[id]) partnerHistory[id] = new Set();
          if (!opponentHistory[id]) opponentHistory[id] = new Set();
          if (!homeAwayCount[id]) homeAwayCount[id] = { home: 0, away: 0 };
        });
        partnerHistory[t1Ids[0]].add(t1Ids[1]);
        partnerHistory[t1Ids[1]].add(t1Ids[0]);
        partnerHistory[t2Ids[0]].add(t2Ids[1]);
        partnerHistory[t2Ids[1]].add(t2Ids[0]);
        // Track phase-internal partnerships only from R1
        if (round === 1) {
          phasePartners[t1Ids[0]].add(t1Ids[1]);
          phasePartners[t1Ids[1]].add(t1Ids[0]);
          phasePartners[t2Ids[0]].add(t2Ids[1]);
          phasePartners[t2Ids[1]].add(t2Ids[0]);
        }
        t1Ids.forEach((a) =>
          t2Ids.forEach((b) => {
            opponentHistory[a].add(b);
            opponentHistory[b].add(a);
          }),
        );
        t1Ids.forEach((id) => homeAwayCount[id].home++);
        t2Ids.forEach((id) => homeAwayCount[id].away++);
      });

      drawnMatches.push({ round, matches: roundMatches, _used: 0 });
    }

    drawnByGroup[g.letter] = drawnMatches;
  });

  return matches.map((m) => {
    if (m.phase !== phase) return m;
    if (!m.needsDraw) return m;

    const groupDraw = drawnByGroup[m.primaryGroup];
    if (!groupDraw) return m;

    const roundDraw = groupDraw.find((r) => r.round === m.round);
    if (!roundDraw) return m;

    const drawnMatch = roundDraw.matches[roundDraw._used];
    if (!drawnMatch) return m;
    roundDraw._used++;

    return {
      ...m,
      team1: {
        p1: concreteSlot(drawnMatch.team1[0], m.primaryGroup, m.phase),
        p2: concreteSlot(drawnMatch.team1[1], m.primaryGroup, m.phase),
      },
      team2: {
        p1: concreteSlot(drawnMatch.team2[0], m.primaryGroup, m.phase),
        p2: concreteSlot(drawnMatch.team2[1], m.primaryGroup, m.phase),
      },
      needsDraw: false,
    };
  });
}

// ------------------------------------------------------------
// PROMOTION / DEMOTION
//
// After each phase: top N from group i+1 ↔ bottom N from group i
// promotionCount governs how many are promoted/demoted
// ------------------------------------------------------------
export function applyPromotionDemotion(
  groups,
  standings,
  promotionCount = 3,
  matches = [],
  rankingOrder = null,
) {
  const ranked = groups.map((g) => ({
    letter: g.letter,
    sorted: [...g.players].sort((a, b) =>
      compareForRanking(a, b, standings, matches, rankingOrder),
    ),
  }));

  const newGroups = ranked.map((g) => ({ letter: g.letter, players: [...g.sorted] }));
  const n = promotionCount;

  for (let i = 0; i < newGroups.length - 1; i++) {
    const upper = newGroups[i];
    const lower = newGroups[i + 1];
    if (upper.players.length < n * 2 || lower.players.length < n * 2) continue;

    // Promotion: top N from the lower, sorted by placement (winner first)
    const promoted = lower.players.slice(0, n);
    // Demotion: bottom N from the upper (10th, 11th, 12th place), sorted best first
    const demoted = upper.players.slice(-n);

    // New upper: stayers (1st–9th place) + newcomers (winner of the lower = pos 10)
    upper.players = [...upper.players.slice(0, -n), ...promoted];
    // New lower: the demoted (10th, 11th, 12th from the upper = pos 1, 2, 3) + stayers
    lower.players = [...demoted, ...lower.players.slice(n)];
  }

  return newGroups;
}

// ------------------------------------------------------------
// PER-BLOCK PROMOTION/DEMOTION (context-aware)
// ------------------------------------------------------------
// Draws the next phase for one block (e.g. {E, F}), but respects
// the chain dependency by INCLUDING neighboring groups in the run:
//   - The group directly above the block (if it exists) — to know who
//     is demoted DOWN to the block
//   - The group directly below the block (if it exists) — to know who
//     is promoted UP to the block
//
// Precondition: all "context groups" (block + neighbors) MUST have completed
// their current phase. The caller must verify this first.
//
// Returns: a new full group array where ONLY the block groups are updated.
// The neighboring groups used in the computation are NOT written back — they
// are drawn when their own block button is used.
export function applyBlockPromotionDemotion(
  groups,
  blockLetters,
  standings,
  promotionCount = 3,
  matches = [],
  rankingOrder = null,
) {
  if (blockLetters.length === 0) return groups;

  // Find the indices in the global group array
  const blockIndices = blockLetters
    .map((letter) => groups.findIndex((g) => g.letter === letter))
    .filter((idx) => idx >= 0)
    .sort((a, b) => a - b);

  if (blockIndices.length === 0) return groups;

  const firstIdx = blockIndices[0];
  const lastIdx = blockIndices[blockIndices.length - 1];

  // Context = the block + the group directly above (if it exists) + the group directly below (if it exists)
  const contextStart = Math.max(0, firstIdx - 1);
  const contextEnd = Math.min(groups.length - 1, lastIdx + 1);
  const contextGroups = groups.slice(contextStart, contextEnd + 1);

  // Sort the players in each context group
  const ranked = contextGroups.map((g) => ({
    letter: g.letter,
    sorted: [...g.players].sort((a, b) =>
      compareForRanking(a, b, standings, matches, rankingOrder),
    ),
  }));

  // Run the same chain promotion as applyPromotionDemotion, but only within the context window
  const newContextGroups = ranked.map((g) => ({ letter: g.letter, players: [...g.sorted] }));
  const n = promotionCount;
  for (let i = 0; i < newContextGroups.length - 1; i++) {
    const upper = newContextGroups[i];
    const lower = newContextGroups[i + 1];
    if (upper.players.length < n * 2 || lower.players.length < n * 2) continue;
    const promoted = lower.players.slice(0, n);
    const demoted = upper.players.slice(-n);
    upper.players = [...upper.players.slice(0, -n), ...promoted];
    lower.players = [...demoted, ...lower.players.slice(n)];
  }

  // WRITE BACK: ONLY the block groups are updated. The neighboring groups remain
  // unchanged in the final state — they are written out when their own block is drawn.
  const newGroupByLetter = {};
  newContextGroups.forEach((g) => {
    if (blockLetters.includes(g.letter)) newGroupByLetter[g.letter] = g;
  });
  return groups.map((g) => newGroupByLetter[g.letter] || g);
}

// ------------------------------------------------------------
// FINAL RANKING
// ------------------------------------------------------------
export function computeFinalRanking(groups, standings, matches = [], rankingOrder = null) {
  const ranking = [];
  const playersPerGroup = groups[0]?.players.length || 12;
  groups.forEach((g, gIdx) => {
    const sorted = [...g.players].sort((a, b) =>
      compareForRanking(a, b, standings, matches, rankingOrder),
    );
    sorted.forEach((p, idx) => {
      ranking.push({
        position: gIdx * playersPerGroup + idx + 1,
        player: p,
        groupLetter: g.letter,
        points: standings[p.id]?.points || 0,
        wins: standings[p.id]?.wins || 0,
        matchesPlayed: standings[p.id]?.matchesPlayed || 0,
      });
    });
  });
  return ranking;
}

// ------------------------------------------------------------
// HELPER: get the player from a slot (does a lookup in the player database)
// ------------------------------------------------------------
export function slotPlayer(slot, allPlayers) {
  if (slot.kind !== 'concrete') return null;
  return allPlayers.find((p) => p.id === slot.playerId);
}

export function teamElo(team, allPlayers) {
  const p1 = slotPlayer(team.p1, allPlayers);
  const p2 = slotPlayer(team.p2, allPlayers);
  if (!p1 || !p2) return null;
  return Math.round((p1.elo + p2.elo) / 2);
}

// ─────────────────────────────────────────────────────────────────────
// SLOT / TEAM / DISPLAY-ID HELPERS — moved verbatim out of legacy/App.jsx
// (incremental split). These are the pure slot-model accessors and the
// UI display-id derivations used across the archive views. No React, no
// storage. The former monolith re-imported them from here (getTeamPlayerIds
// alone was used many times across the monolith). Inline comments were
// originally in Norwegian.
// ─────────────────────────────────────────────────────────────────────

function getPlayerIdFromSlot(slotOrFlat) {
  if (!slotOrFlat) return null;
  // Slot format
  if (slotOrFlat.kind === 'concrete') return slotOrFlat.playerId;
  // Flat format
  return slotOrFlat.playerId || slotOrFlat.id || null;
}

// Helper: get the player's originGroup if available
export function getSlotGroup(slot) {
  if (!slot) return null;
  if (slot.kind === 'concrete') return slot.originGroup;
  if (slot.kind === 'placeholder') return slot.fromGroup;
  return null;
}

// Helper: get team info from match.team1/team2 — handles both slot format and flat
export function getTeamPlayerIds(team) {
  if (!team) return [null, null];
  // Padel24 slot format: { p1: slot, p2: slot }
  if (team.p1 && (team.p1.kind || team.p1.playerId)) {
    return [getPlayerIdFromSlot(team.p1), getPlayerIdFromSlot(team.p2)];
  }
  // Flat: { p1Id, p2Id }
  return [team.p1Id, team.p2Id];
}

// Get the display ID for a tournament. Uses the stored displayId if set; otherwise
// falls back to a short hash of the internal ID.
export function getDisplayTournamentId(tournament) {
  if (!tournament) return '?';
  if (tournament.displayId) return tournament.displayId;
  // Fallback for legacy tournaments without a displayId — show the last 4 chars of the ID
  const id = tournament.id || tournament.archiveId;
  if (!id) return '?';
  return 'T·' + id.slice(-4);
}

// Get the display ID for a match. M1, M2, ... based on position in tournament.matches.
// Sorting uses the array's current order (the engine adds matches in
// chronological order during generateRound).
export function getDisplayMatchId(tournament, match) {
  if (!tournament || !match) return '?';
  const idx = (tournament.matches || []).findIndex((m) => m.id === match.id);
  if (idx < 0) return match.id || '?';
  return `M${idx + 1}`;
}

// KM/seriespill match → the common row format that UniversalMatchRow understands.
function kmSeriesMatchToRow(m, pairs) {
  const list = pairs || [];
  const findPair = (pairId, divisionId) =>
    list.find((p) => p.id === pairId && p.divisionId === divisionId) ||
    list.find((p) => p.id === pairId);
  const mkSlot = (pid) => ({ kind: 'concrete', playerId: pid });
  const a = findPair(m.team1?.pairId, m.divisionId);
  const b = findPair(m.team2?.pairId, m.divisionId);
  // Seriespill matches (and KM group matches) have the player IDs directly on the match;
  // some KM matches have only a pairId and require a pair lookup. So use the direct
  // IDs first, with the pair lookup as a fallback.
  const a1 = m.team1?.p1Id ?? a?.p1Id;
  const a2 = m.team1?.p2Id ?? a?.p2Id;
  const b1 = m.team2?.p1Id ?? b?.p1Id;
  const b2 = m.team2?.p2Id ?? b?.p2Id;
  const pAt = m.playedAt || m.completedAt || m.scoredAt || null;
  const d = pAt ? new Date(pAt) : null;
  const valid = d && !isNaN(d.getTime());
  const pad = (n) => String(n).padStart(2, '0');
  return {
    id: m.id,
    team1: { p1: mkSlot(a1), p2: mkSlot(a2) },
    team2: { p1: mkSlot(b1), p2: mkSlot(b2) },
    score1: m.score?.t1,
    score2: m.score?.t2,
    completed: true,
    phase: m.phase === 'finals' ? 'finals' : 'group',
    round: m.roundIndex || m.round || null,
    court: m.court || null,
    date: valid ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` : '',
    timeMinutes: valid ? d.getHours() * 60 + d.getMinutes() : 0,
    eloChange1: m.teamEloChange?.t1 ?? 0,
    eloChange2: m.teamEloChange?.t2 ?? 0,
    teamElo1Pre: m.teamEloPre?.t1 != null ? Math.round(m.teamEloPre.t1) : null,
    teamElo2Pre: m.teamEloPre?.t2 != null ? Math.round(m.teamEloPre.t2) : null,
    playerEloPre: m.playerEloPre || {},
  };
}
// kmSeriesMatchToRow is exported below (after getTeamPlayerIds) so the former
// monolith and the archive components share a single definition.
export { kmSeriesMatchToRow };

// getTournamentWinners — pure winner/runner-up derivation for a finished
// tournament/activity. Moved verbatim from legacy/App.jsx; the only change is
// `Padel24Engine.americano.calcStandings(t)` → the named `calcAmericanoStandings(t)`
// import (same function), and `getTeamPlayerIds` is now the local export above.
export function getTournamentWinners(t) {
  if (!t || t.isVirtual || !t.matches) return null;
  const isDone = t.status === 'done' || t.isArchived;
  if (!isDone) return null;

  const playerById = {};
  (t.players || []).forEach((p) => {
    playerById[p.id] = p;
  });

  // Americano/Mexicano/Mixicano/Team Mexicano/Mix Americano with standings mode
  if (
    (t.type === 'americano' ||
      t.type === 'mexicano' ||
      t.type === 'mixicano' ||
      t.type === 'team_mexicano' ||
      t.type === 'mix_americano' ||
      t.type === 'club_americano' ||
      t.type === 'club_team_americano' ||
      t.type === 'club_mexicano') &&
    t.config?.winnerSource === 'standings'
  ) {
    try {
      const standings = calcAmericanoStandings(t);
      const active = standings.filter((s) => !s.injured);
      if (active.length === 0) return null;
      const topRank = active[0].rank;
      const secondRank = active.find((s) => s.rank > topRank)?.rank;
      const winners = active
        .filter((s) => s.rank === topRank)
        .map((s) => playerById[s.playerId])
        .filter(Boolean);
      const runnersUp = secondRank
        ? active
            .filter((s) => s.rank === secondRank)
            .map((s) => playerById[s.playerId])
            .filter(Boolean)
        : [];
      return { winners, runnersUp };
    } catch (_e) {
      return null;
    }
  }

  // Finals mode (both Americano and Padel24): finale 1 decides the winner
  const topFinal = (t.matches || []).filter(
    (m) => m.isFinal && m.completed && m.finalNumber === 1,
  )[0];
  if (!topFinal) return null;

  const [t1p1, t1p2] = getTeamPlayerIds(topFinal.team1);
  const [t2p1, t2p2] = getTeamPlayerIds(topFinal.team2);
  const t1Won = topFinal.score1 > topFinal.score2;
  const winnerIds = t1Won ? [t1p1, t1p2] : [t2p1, t2p2];
  const loserIds = t1Won ? [t2p1, t2p2] : [t1p1, t1p2];
  const winners = winnerIds.map((id) => playerById[id]).filter(Boolean);
  const runnersUp = loserIds.map((id) => playerById[id]).filter(Boolean);
  return { winners, runnersUp };
}
